import { isUnderlined } from '@nexmail/shared-util';
import type { ListType, RichTextSpan } from '@nexmail/shared-util';
import type { BrandResolverContext } from '@nexmail/shared-util/email-builder';
import type { SceneNode, SceneDocument, ImageTransform } from '@/lib/scene-document';

/**
 * Zustand store for the CanvasKit interactive editor.
 * Holds document state, selection, interaction mode, viewport, and history.
 * Created per editor instance (not a global singleton).
 */
import { createStore } from 'zustand';

import { ensureArtboardRoot } from './canvas-artboard';
import { newClipboardStamp } from './canvas-clipboard';
import { getCachedImage } from './canvaskit-render-scene';
import { computeSnap, type SnapGuide } from './canvaskit-snap';
import { invalidateOutlineCache } from './canvaskit-text-warp';
import { computeLayout, commitDocSize, tightenGroups } from './canvaskit-layout';
import {
  alignNodes,
  tidyUpNodes,
  type AlignEdge,
  distributeNodes,
  type DistributeAxis,
} from './canvaskit-align';
import {
  bandStack,
  restackBands,
  isAbsoluteArtboard,
  reorderChildrenToStack,
  duplicateBandBelow,
} from './canvas-band-restack';
import {
  type Point,
  getNodeAABB,
  marqueeSelect,
  getSelectionAABB,
  getSvgPathBounds,
  findDropContainerForNode,
  resolveInsertParent,
  getNodeWorldBounds,
  getParentWorldOffset,
} from './canvaskit-hit-test';
import {
  panTransform,
  zoomTransform,
  computeCropResize,
  resolveCropTarget,
  readCropTransform,
  cropTransformPatch,
  computeDefaultTransform,
} from './canvaskit-crop';
import {
  computeResize,
  type HandleId,
  computeRotation,
  nodeCornerRadii,
  computeStartAngle,
  getEnabledHandles,
  type RadiusHandleId,
  scaleNodeBoundsInAABB,
  computeCornerRadiusFromPoint,
} from './canvaskit-transform';
import {
  snapToGrid,
  simplifyPath,
  type EdgeHit,
  fitFreehandPath,
  type VectorPath,
  type VectorPoint,
  rectToVectorPaths,
  sealToVectorPaths,
  starToVectorPaths,
  type VectorPointHit,
  vectorPathsToSvgPath,
  insertPointOnSegment,
  ellipseToVectorPaths,
  polygonToVectorPaths,
  parseSvgToVectorPaths,
  trapezoidToVectorPaths,
  parseSvgPathToVectorPaths,
} from './canvaskit-vector-points';
import {
  generateNodeId,
  findZoneNeighbors,
  addNode as mutAddNode,
  updateNode as mutUpdateNode,
  moveZOrder as mutMoveZOrder,
  groupNodes as mutGroupNodes,
  cloneNodes as mutCloneNodes,
  duplicateAsSiblings as mutDuplicateAsSiblings,
  updateNodes as mutUpdateNodes,
  removeNodes as mutRemoveNodes,
  ungroupNode as mutUngroupNode,
  addLinkZone as mutAddLinkZone,
  reparentNode as mutReparentNode,
  addClonedNodes as mutAddClonedNodes,
  removeLinkZone as mutRemoveLinkZone,
  moveNodeToIndex as mutMoveNodeToIndex,
  getLinkZoneNodes as mutGetLinkZoneNodes,
  setLinkZoneLayout as mutSetLinkZoneLayout,
  addSceneDocumentAsGroup as mutAddSceneDocumentAsGroup,
} from './canvaskit-mutations';

import type { CropHandleId } from './canvaskit-crop';

// ─── Types ────────────────────────────────────────────────────────────────

export type InteractionMode =
  | 'idle'
  | 'dragging'
  | 'resizing'
  | 'rotating'
  | 'radiusDragging'
  | 'marquee'
  | 'panning'
  | 'cropping'
  | 'cropResizing'
  | 'drawing'
  | 'vectorDragging';

export type DrawToolType =
  | 'RECTANGLE'
  | 'ELLIPSE'
  | 'STAR'
  | 'POLYGON'
  | 'TRAPEZOID'
  | 'SEAL'
  | 'LINE'
  | 'PEN'
  | 'FREEHAND';

export interface CanvasEditorState {
  // Document
  doc: SceneDocument;

  // Active brand resolver context. The doc stores brand tokens (e.g.
  // `$brand.color.primary`) RAW in SOLID paint colors; the render path resolves
  // them against this context for display (see canvas-brand-resolve.ts). Null
  // when no brand is available — token colors then fall back to a debug color.
  // Never affects layout/measure, persistence, undo, or the embedded scene.
  brandContext: BrandResolverContext | null;

  // Selection
  selectedIds: Set<string>;
  hoveredId: string | null;
  enteredGroupId: string | null;

  // Interaction
  interactionMode: InteractionMode;
  activeHandle: HandleId | null;
  activeRadiusHandle: RadiusHandleId | null;
  dragStart: Point | null;
  dragNodeStarts: Map<
    string,
    { x: number; y: number; aabb: { x: number; y: number; width: number; height: number } }
  >;
  resizeStartBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  } | null;
  // Per-node start geometry for a 2+-node bounding-box resize. When set, the
  // resize gesture scales each of these nodes inside the combined selection
  // AABB (resizeStartBounds holds that box). Null for single-node resize,
  // which drives the node's own geometry directly. `parentOffset` is captured
  // once so scaled world bounds convert back to local coords without re-running
  // layout mid-drag (parents of the scaled top-level nodes stay put).
  multiResizeStarts: Map<
    string,
    { x: number; y: number; width: number; height: number; parentOffset: Point }
  > | null;
  rotationStartAngle: number;
  marqueeStart: Point | null;
  marqueeRect: { x: number; y: number; w: number; h: number } | null;
  snapGuides: SnapGuide[];

  // Reorder drag. Set by startDrag when the single dragged node's parent has
  // layoutMode VERTICAL or HORIZONTAL (Figma-native auto-layout reorder), OR
  // when the dragged node is a top-level band of an absolutely-positioned
  // email artboard (`absolute: true` — emails are a vertical stack even when
  // bands are hand-placed; see canvas-band-restack.ts). While active,
  // updateDrag tracks the target index and the cursor delta without mutating
  // the doc; the renderer paints a preview (siblings shifted to target index,
  // dragged node ghost-rendered at cursor). endDrag commits via
  // moveNodeToIndex (auto-layout) or restackBands (absolute).
  dragReorder: {
    parentId: string;
    draggedId: string;
    layoutMode: 'VERTICAL' | 'HORIZONTAL';
    fromIndex: number;
    toIndex: number;
    deltaX: number;
    deltaY: number;
    // Absolute-artboard band reorder: indices refer to the y-sorted band
    // stack, not the children array; commit restacks y positions.
    absolute?: boolean;
  } | null;

  // Free-drag nest target. During a (non-reorder) free drag, this is the
  // FRAME/GROUP under the cursor that the dragged node would be reparented
  // into on release. The renderer highlights it; endDrag reparents.
  dragDropTarget: string | null;

  // Viewport
  zoom: number;
  panOffset: Point;
  panStart: Point | null;
  panOffsetStart: Point | null;

  // History
  undoStack: SceneDocument[];
  redoStack: SceneDocument[];

  // Clipboard. These hold the RAW copied nodes (brand tokens intact) and back a
  // same-canvas paste. The system clipboard carries a brand-baked copy of the
  // same selection for cross-document paste -- see canvas-clipboard.
  clipboardNodes: Record<string, SceneNode> | null;
  clipboardTopIds: string[];
  /** Identifies the copy that filled the fields above, so a paste can tell this
   *  editor's own copy from one that arrived via the system clipboard. */
  clipboardStamp: string | null;

  // Inline text editing
  editingTextId: string | null;

  // Image crop mode
  croppingNodeId: string | null;
  /** Index into the cropping node's fills when cropping a shape's IMAGE fill;
   *  null when cropping an IMAGE node's own imageTransform. */
  croppingFillIndex: number | null;
  cropDragStart: Point | null;
  cropStartTransform: ImageTransform | null;
  cropResizeHandle: CropHandleId | null;
  cropStartFrame: { x: number; y: number; width: number; height: number } | null;

  // Draw tool
  drawTool: DrawToolType | null;
  drawingNodeId: string | null;
  drawOrigin: Point | null;

  // Vector editing
  vectorEditingNodeId: string | null;
  vectorPaths: VectorPath[] | null;
  vectorSelectedPoints: Set<string>; // "pathIdx:pointIdx" keys
  vectorDragTarget: VectorPointHit | null;
  vectorDragStart: Point | null;
  vectorDragPointStart: { x: number; y: number } | null;

  // Pen tool
  penPaths: VectorPath[] | null;
  penCurrentPath: VectorPath | null;
  penDragging: boolean;

  // Freehand tool
  freehandPoints: Point[] | null;

  // Vector point grid snap
  vectorGridSize: number;

  // Link zone visibility
  linkZonesVisible: boolean;

  // Layout-guide (column) master visibility — Figma's "Show/Hide layout grids".
  // Session-only, not persisted: per-artboard guide configs live in the
  // artboard frame's pluginData.layoutGuides and carry their own `visible`
  // flag; this flag hides/shows every guide at once without touching them.
  guidesVisible: boolean;

  // Alt held over the canvas with a live selection: the chrome draws Figma
  // measurement lines between the selection box and the hovered (non-selected)
  // node. Ephemeral UI flag toggled by the keyboard handler; only a hover
  // affordance, so it is ignored while an interaction (drag/resize) is active.
  altMeasureActive: boolean;

  // Render trigger
  renderVersion: number;
}

export interface CanvasEditorActions {
  // Init
  setDocument(doc: SceneDocument): void;
  /**
   * Update the active brand resolver context used to resolve `$brand.color.*`
   * tokens for display. No-ops (no render) when the reference is unchanged.
   * Bumps renderVersion otherwise so a brand swap re-themes the canvas.
   */
  setBrandContext(ctx: BrandResolverContext | null): void;

  // Selection
  select(nodeId: string): void;
  selectMultiple(nodeIds: string[]): void;
  toggleSelect(nodeId: string): void;
  clearSelection(): void;
  setHovered(nodeId: string | null): void;
  enterGroup(groupId: string): void;
  exitGroup(): void;
  /** Select every child of the entered group, else all root-level nodes (Cmd/Ctrl+A). */
  selectAll(): void;
  /** Select the next (+1) or previous (-1) sibling of the single selection (Tab / Shift+Tab). */
  selectSibling(direction: number): void;
  /** Enter the single-selected container and select its first child (Enter). */
  descendSelection(): void;
  /** Select the parent of the single selection (Shift+Enter). */
  ascendSelection(): void;

  // Drag
  startDrag(scenePoint: Point, opts?: { skipHistory?: boolean }): void;
  updateDrag(scenePoint: Point, shiftKey: boolean, disableSnap?: boolean): void;
  endDrag(): void;
  /**
   * Alt+drag: clone the current selection in place and begin dragging the
   * copies (originals stay put). Pushes ONE history entry for the whole
   * gesture; the drag reuses it via startDrag({ skipHistory: true }).
   */
  beginAltDragDuplicate(scenePoint: Point): void;

  // Resize
  startResize(handle: HandleId, scenePoint: Point): void;
  updateResize(scenePoint: Point, shiftKey: boolean, altKey?: boolean): void;
  endResize(): void;

  // Rotate
  startRotate(scenePoint: Point): void;
  updateRotate(scenePoint: Point, shiftKey: boolean): void;
  endRotate(): void;

  // Corner radius (Figma-style inset circle handles)
  startRadiusDrag(handle: RadiusHandleId, scenePoint: Point): void;
  updateRadiusDrag(scenePoint: Point, altKey: boolean): void;
  endRadiusDrag(): void;

  // Marquee
  startMarquee(scenePoint: Point): void;
  updateMarquee(scenePoint: Point): void;
  endMarquee(): void;

  // Pan
  startPan(screenPoint: Point): void;
  updatePan(screenPoint: Point): void;
  endPan(): void;

  // Draw tool
  setDrawTool(tool: DrawToolType | null): void;
  startDrawing(scenePoint: Point): void;
  updateDrawing(scenePoint: Point, shiftKey: boolean): void;
  endDrawing(): void;
  cancelDrawing(): void;

  // Document mutations
  updateNodeProp(nodeId: string, patch: Partial<SceneNode>): void;
  /**
   * Apply several node patches as ONE undoable step (single history push,
   * one render). Used by product-block swaps, where image + title + price +
   * links rewrite together and must undo together.
   */
  applyNodePatches(patches: Array<{ nodeId: string; props: Partial<SceneNode> }>): void;
  /**
   * Explicit "Convert to auto-layout" for a free-form section frame — the
   * per-frame form of the load-time stacked-group upgrade, only applied when
   * its safety checks pass (vertically stacked, consistently aligned
   * children). Returns true when the frame was upgraded; false means the
   * layout is too free-form to convert automatically.
   */
  tryUpgradeFrameToAutoLayout(frameId: string): boolean;
  addNewNode(node: SceneNode, parentId?: string): void;
  removeSelected(): void;
  moveZOrder(direction: 'forward' | 'backward' | 'toFront' | 'toBack'): void;
  moveNodeToIndex(nodeId: string, newIndex: number): void;
  reparentNode(nodeId: string, newParentId: string | null, insertIndex: number): void;
  groupSelected(): void;
  ungroupSelected(): void;
  /** Wrap the current selection in a new FRAME sized to its AABB, keeping each
   *  child's on-screen position (Cmd/Ctrl+Opt+G). */
  frameSelected(): void;
  nudge(dx: number, dy: number): void;
  /** Align selected nodes' bounding boxes to a shared edge/center (needs 2+). */
  alignSelected(edge: AlignEdge): void;
  /** Equalize gaps between selected nodes along an axis (needs 3+). */
  distributeSelected(axis: DistributeAxis): void;
  /** Snap a loose selection into a clean aligned + evenly-spaced line. */
  tidyUpSelected(): void;
  /**
   * Toggle the single-selected node between auto-layout flow and free-floating
   * (ABSOLUTE) positioning, preserving its on-screen position when floating so
   * it can sit over a sibling (e.g. text over an image). Docking returns it to
   * the stack, where layout recomputes its position.
   */
  toggleSelectedFloating(): void;
  /**
   * Add or remove auto-layout on the selected frame(s) — Figma's Shift+A /
   * Opt+Shift+A. Turning it OFF pins the frame to its current size (a free
   * frame can't hug) and bakes children at their laid-out geometry so nothing
   * jumps; turning it ON restores hug and flows the children. `direction` lets
   * the properties panel pick H vs V when enabling.
   */
  setSelectedAutoLayout(on: boolean, direction?: 'VERTICAL' | 'HORIZONTAL'): void;
  /**
   * Mirror the selected container's LAYOUT horizontally (image|text →
   * text|image): reverse children for HORIZONTAL auto-layout rows, mirror
   * child x positions for absolute frames. Content is never mirrored.
   */
  flipSelectedLayout(): void;
  replaceImageWithVectorizedSvg(
    nodeId: string,
    svg: string,
    sourceWidth: number,
    sourceHeight: number
  ): void;

  // Document-level
  updateDocumentSize(width: number, height: number): void;
  updateDocumentFills(fills: NonNullable<SceneDocument['fills']>): void;
  addTemplateAsGroup(incoming: SceneDocument, name: string): void;

  // Link zones
  addLinkZone(): void;
  removeLinkZone(nodeId: string): void;
  setLinkZoneLayout(layoutId: string): void;
  getLinkZoneNodes(): SceneNode[];
  setLinkZonesVisible(visible: boolean): void;
  toggleGuidesVisible(): void;

  // Clipboard
  copySelected(): void;
  /** Paste this editor's own clipboard nodes, brand tokens intact. With
   *  `centerPoint` (world coords) the pasted selection is centered there
   *  (paste-at-cursor); otherwise it offsets +10/+10. */
  pasteClipboard(centerPoint?: Point): void;
  /** Paste nodes that arrived from another document via the system clipboard.
   *  They come brand-baked (literal colors) and carry their source ids, which
   *  are re-minted here. `stamp` is adopted so a repeat paste cascades. */
  pasteExternalNodes(
    nodes: Record<string, SceneNode>,
    topIds: string[],
    stamp: string,
    centerPoint?: Point,
  ): void;
  duplicateSelected(): void;

  // Viewport
  setZoom(zoom: number, focalScreenPoint?: Point): void;
  /** Frame the whole document in the viewport (Shift+1). Does not enlarge past 100%. */
  zoomToFit(viewportWidth: number, viewportHeight: number): void;
  /** Frame the current selection in the viewport (Shift+2). Falls back to the whole document when nothing is selected. */
  zoomToSelection(viewportWidth: number, viewportHeight: number): void;

  // History
  pushHistory(): void;
  undo(): void;
  redo(): void;

  // Image crop mode
  enterCropMode(nodeId: string): void;
  exitCropMode(): void;
  startCropDrag(scenePoint: Point): void;
  updateCropDrag(scenePoint: Point): void;
  endCropDrag(): void;
  startCropResize(handle: CropHandleId, scenePoint: Point): void;
  updateCropResize(scenePoint: Point): void;
  endCropResize(): void;
  /** Zoom the cropped image around a scene-space anchor (scroll wheel). */
  zoomCroppedImage(scenePoint: Point, factor: number): void;

  // Vector editing
  enterVectorEditMode(nodeId: string): void;
  exitVectorEditMode(): void;
  selectVectorPoint(pathIndex: number, pointIndex: number): void;
  toggleVectorPoint(pathIndex: number, pointIndex: number): void;
  startVectorDrag(target: VectorPointHit, scenePoint: Point): void;
  updateVectorDrag(scenePoint: Point): void;
  endVectorDrag(): void;
  deleteVectorPoints(): void;
  updateVectorPaths(newPaths: VectorPath[]): void;
  addPointOnEdge(edge: EdgeHit): void;

  // Pen tool
  penToolClick(scenePoint: Point): void;
  penToolDrag(scenePoint: Point): void;
  penToolEndDrag(): void;
  penToolFinish(): void;

  // Text to outlines (requires CanvasKit instance)
  textToOutlines(nodeId: string, ck: any, fontMgr: any): void;

  // Boolean operations (requires CanvasKit instance)
  booleanOp(op: 'union' | 'subtract' | 'intersect' | 'xor', ck: any): void;

  // Freehand tool
  freehandStart(scenePoint: Point): void;
  freehandMove(scenePoint: Point): void;
  freehandEnd(): void;

  // Path simplification
  simplifyVectorPaths(tolerance: number): void;

  // Grid snap
  setVectorGridSize(size: number): void;
  snapSelectedPointsToGrid(): void;

  // SVG import
  importSvg(svgString: string): void;

  // Copy/paste vector points
  copyVectorPoints(): void;
  pasteVectorPoints(): void;

  // Inline text editing
  startTextEdit(nodeId: string): void;
  commitTextEdit(
    newText: string,
    measuredHeight?: number,
    spans?: RichTextSpan[],
    list?: ListType | null,
    measuredWidth?: number
  ): void;
  cancelTextEdit(): void;

  // Render
  requestRender(): void;

  // Computed
  getSelectedNodes(): SceneNode[];
}

// ─── Constants ────────────────────────────────────────────────────────────

const MAX_HISTORY = 50;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;
const FIT_PADDING = 40;

// Compute the zoom + panOffset that centers `bounds` (scene-space) inside a
// viewport of the given CSS pixel size, leaving FIT_PADDING px of margin.
// `maxZoom` caps how far we enlarge: fit-document passes 1 (never blow past
// 100%); fit-selection passes a higher cap so small selections zoom in.
// Screen-space mapping is `screen = scene * zoom + panOffset`.
function computeFitView(
  bounds: { x: number; y: number; width: number; height: number },
  viewportW: number,
  viewportH: number,
  maxZoom: number
): { zoom: number; panOffset: Point } {
  const w = Math.max(1, bounds.width);
  const h = Math.max(1, bounds.height);
  const scaleX = (viewportW - FIT_PADDING * 2) / w;
  const scaleY = (viewportH - FIT_PADDING * 2) / h;
  const zoom = Math.max(MIN_ZOOM, Math.min(scaleX, scaleY, maxZoom));
  const panX = (viewportW - w * zoom) / 2 - bounds.x * zoom;
  const panY = (viewportH - h * zoom) / 2 - bounds.y * zoom;
  return { zoom, panOffset: { x: panX, y: panY } };
}

// ─── Pen tool helper ──────────────────────────────────────────────────────

function finishPenPath(
  get: () => CanvasEditorState & CanvasEditorActions,
  set: (patch: Partial<CanvasEditorState>) => void,
  path: VectorPath
) {
  const { doc } = get();
  get().pushHistory();

  const pathData = vectorPathsToSvgPath([path]);
  const nodeId = generateNodeId();
  const node: SceneNode = {
    id: nodeId,
    type: 'PATH',
    parentId: null,
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
    pathData,
    fills: [{ type: 'SOLID', color: '#D1D5DB' }],
    strokes: [{ type: 'SOLID', color: '#000000' }],
    strokeWeight: 2,
    name: 'pen-path',
  };

  const nextDoc = mutAddNode(doc, node);
  set({
    doc: nextDoc,
    penCurrentPath: null,
    drawTool: null,
    selectedIds: new Set([nodeId]),
    renderVersion: get().renderVersion + 1,
  } as any);
}

// ─── Doc-size commit ─────────────────────────────────────────────────────

/**
 * Run the layout pass and, if the laid-out root wrapper has different
 * dimensions than the doc, write the new dimensions back. Used at the end of
 * mutations that can grow/shrink an auto-layout wrapper (resize, reorder,
 * add/remove child) so saved docs reflect what was just rendered.
 *
 * Multi-root docs (the infinite-canvas workspace, where each root child is a
 * free-positioned per-canvas FRAME) have no single wrapper to size to, but
 * their nested email frames still need their auto-layout baked in so the
 * reordered/resized section positions persist after the gesture — otherwise
 * the workspace renderer would re-derive them every frame but they'd never be
 * saved. Fall back to baking layout without the doc-size sync.
 */
function commitRootSize(doc: SceneDocument): SceneDocument {
  return commitDocSize(doc);
}

/**
 * Given a TEXT node's `textAutoResize` intent plus freshly measured content
 * dimensions (scene units), return the width/height patch the editor should
 * apply. Shared by the live grow-while-typing path and the on-blur commit so
 * both size the box the same way.
 *
 *   AUTO_HEIGHT — width fixed; height tracks the measured text exactly (grows
 *                 AND shrinks).
 *   AUTO_WIDTH  — both derive from the unwrapped content (longest line width,
 *                 natural height).
 *   FIXED       — box locked; never auto-grows.
 *   undefined   — legacy behaviour: grow height only, never shrink. Kept
 *                 byte-for-byte so docs without the field are unaffected.
 *
 * `contentW` is only meaningful for AUTO_WIDTH (the overlay must have measured
 * it unwrapped); the other modes ignore it.
 */
export function textAutoResizeDimPatch(
  mode: SceneNode['textAutoResize'],
  node: { width: number; height: number },
  contentW: number,
  contentH: number,
): { width?: number; height?: number } {
  const h = Math.ceil(contentH);
  const w = Math.ceil(contentW);
  switch (mode) {
    case 'AUTO_HEIGHT':
      return h !== node.height ? { height: h } : {};
    case 'AUTO_WIDTH': {
      const patch: { width?: number; height?: number } = {};
      if (w > 0 && w !== node.width) patch.width = w;
      if (h !== node.height) patch.height = h;
      return patch;
    }
    case 'FIXED':
      return {};
    default:
      return contentH > node.height ? { height: h } : {};
  }
}

/**
 * Cross-axis drag distance (scene units) past which a flow child pops out of
 * its auto-layout stack and becomes free-floating (absolute) — "drag it off the
 * row to float it over a sibling" (e.g. text over an image).
 */

/** Collect a node id plus every descendant id (used to exclude a dragged
 *  node's own subtree from nest-target detection). */
function collectSubtree(doc: SceneDocument, rootId: string): Set<string> {
  const out = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop() as string;
    if (out.has(id)) continue;
    out.add(id);
    const node = doc.nodes[id];
    if (node?.children) stack.push(...node.children);
  }
  return out;
}

/**
 * Filter a selection to its top-most nodes: drop any id that is a descendant
 * of another selected id. A multi-select transform drives only the top-most
 * nodes so a node and a selected ancestor aren't both moved (which would
 * double-apply the scale to the descendant).
 */
function topmostSelectedIds(doc: SceneDocument, ids: Iterable<string>): string[] {
  const set = new Set(ids);
  const out: string[] = [];
  for (const id of set) {
    let p = doc.nodes[id]?.parentId ?? null;
    let nested = false;
    while (p) {
      if (set.has(p)) {
        nested = true;
        break;
      }
      p = doc.nodes[p]?.parentId ?? null;
    }
    if (!nested) out.push(id);
  }
  return out;
}

// ─── Auto-layout upgrade for stacked groups ──────────────────────────────

/**
 * Walk every GROUP/FRAME node and, when its children are non-overlapping
 * vertical stacks, upgrade it to layoutMode: 'VERTICAL' with gap derived
 * from the existing spacing. Side-by-side or overlapping layouts are left
 * alone so card-style groups (image + text column) keep their free-form
 * positioning.
 *
 * Lets legacy canvases pick up Figma-style click + drag-reorder behavior at
 * every nesting level on first load.
 */
/**
 * Derive the auto-layout upgrade patch for one GROUP/FRAME whose children
 * form a non-overlapping vertical stack. Returns null when the upgrade is
 * unsafe (side-by-side / overlapping / mixed-alignment children). Shared by
 * the load-time autoUpgradeStackedGroups sweep and the explicit per-frame
 * "Convert to auto-layout" action in the section-mode UI.
 *
 * `force` skips the only-unconfigured/buggy-signature gate — the explicit
 * action should work on any NONE-layout frame the safety checks allow, not
 * just first-load shapes.
 */
function deriveStackedUpgrade(
  node: SceneNode,
  doc: SceneDocument,
  opts: { force?: boolean } = {}
): Partial<SceneNode> | null {
  const TOLERANCE = 4;
  if (node.type !== 'GROUP' && node.type !== 'FRAME') return null;

  // Two paths that share alignment derivation:
  //  - Unconfigured: no layoutMode -> set layoutMode + padding + align
  //  - Buggy-upgrade signature: layoutMode VERTICAL but every padding field
  //    is undefined (an earlier version of this upgrade set the mode but
  //    left alignment defaulting to START with 0 padding, which collapses
  //    centered children to the left edge). Re-derive alignment + padding
  //    only; leave layoutMode alone.
  const unconfigured = !node.layoutMode || node.layoutMode === 'NONE';
  const buggyUpgrade =
    node.layoutMode === 'VERTICAL' &&
    node.layoutPaddingTop === undefined &&
    node.layoutPaddingBottom === undefined &&
    node.layoutPaddingLeft === undefined &&
    node.layoutPaddingRight === undefined;
  if (!unconfigured && !buggyUpgrade && !opts.force) return null;
  if (opts.force && !unconfigured) return null; // already auto-layout — nothing to do
  const childIds = node.children ?? [];
  if (childIds.length < 2) return null;

  const children = childIds.map((id) => doc.nodes[id]).filter((n): n is SceneNode => !!n);
  if (children.length !== childIds.length) return null;

  // Children must be vertically stacked with no overlap.
  const sorted = [...children].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.y - (prev.y + prev.height) < -TOLERANCE) return null;
  }

  // Detect counter-axis alignment so the layout pass doesn't shift content.
  // CENTER: every child's horizontal center matches the container's center.
  // START: every child shares the same x.
  // Anything else (mixed alignment) is unsafe to auto-upgrade — skip.
  const containerCenter = node.width / 2;
  const xCentered = sorted.every((c) => Math.abs(c.x + c.width / 2 - containerCenter) <= TOLERANCE);
  const xStartAnchor = sorted[0].x;
  const xStarted = sorted.every((c) => Math.abs(c.x - xStartAnchor) <= TOLERANCE);

  let counterAxisAlign: 'START' | 'CENTER';
  let layoutPaddingLeft: number;
  let layoutPaddingRight: number;
  if (xCentered) {
    counterAxisAlign = 'CENTER';
    layoutPaddingLeft = 0;
    layoutPaddingRight = 0;
  } else if (xStarted) {
    counterAxisAlign = 'START';
    layoutPaddingLeft = Math.max(0, Math.round(xStartAnchor));
    layoutPaddingRight = 0;
  } else {
    return null;
  }

  // Preserve top/bottom whitespace by treating the first child's y as
  // padding and the post-last gap as bottom padding.
  const layoutPaddingTop = Math.max(0, Math.round(sorted[0].y));
  const lastChild = sorted[sorted.length - 1];
  const layoutPaddingBottom = Math.max(
    0,
    Math.round(node.height - (lastChild.y + lastChild.height))
  );

  // Pick gap that distributes total spacing evenly across children. This
  // preserves the bottom anchor of the last child, which keeps the
  // container's overall visual height stable even when original gaps
  // varied.
  const childHeightSum = sorted.reduce((s, c) => s + c.height, 0);
  const availableForGaps = node.height - layoutPaddingTop - layoutPaddingBottom - childHeightSum;
  const gapDivisor = Math.max(1, sorted.length - 1);
  const computedGap = Math.max(0, Math.round(availableForGaps / gapDivisor));

  return {
    // Only set layoutMode for fresh upgrades; healing preserves whatever
    // mode is already there.
    ...(unconfigured ? { layoutMode: 'VERTICAL' as const } : {}),
    layoutGap: computedGap,
    primaryAxisAlign: node.primaryAxisAlign ?? 'START',
    counterAxisAlign,
    layoutPaddingTop,
    layoutPaddingBottom,
    layoutPaddingLeft,
    layoutPaddingRight,
    // HUG so the wrapper grows when a child is resized larger; the canvas
    // surface follows via syncDocSizeToRoot in the render path.
    layoutSizingVertical: node.layoutSizingVertical ?? 'HUG',
    layoutSizingHorizontal: node.layoutSizingHorizontal ?? 'FIXED',
  };
}

function autoUpgradeStackedGroups(doc: SceneDocument): SceneDocument {
  const upgrades = new Map<string, Partial<SceneNode>>();

  for (const node of Object.values(doc.nodes)) {
    if (!node) continue;
    const patch = deriveStackedUpgrade(node, doc);
    if (patch) upgrades.set(node.id, patch);
  }

  if (upgrades.size === 0) return doc;
  const nextNodes = { ...doc.nodes };
  for (const [id, patch] of upgrades) {
    nextNodes[id] = { ...nextNodes[id], ...patch };
  }
  return { ...doc, nodes: nextNodes };
}

/**
 * Place a set of clipboard nodes into `doc`: re-mint every id, offset the
 * top-level nodes to the cursor (or the default cascade), and insert into the
 * entered group. Shared by both paste paths so nodes from this editor's own
 * clipboard and nodes from another document land identically — the only
 * difference between them is whether brand tokens were baked before they got
 * here.
 *
 * Returns the fresh nodes alongside the doc so the caller can re-seed its
 * clipboard with them and have the next paste cascade again.
 */
function buildPaste(
  rawDoc: SceneDocument,
  clipNodes: Record<string, SceneNode>,
  clipTopIds: string[],
  enteredGroupId: string | null,
  centerPoint?: Point,
): { doc: SceneDocument; nodes: Record<string, SceneNode>; newIds: string[] } | null {
  if (clipTopIds.length === 0) return null;

  // Re-fit groups so the entered group's origin read below is the same one the
  // clones are positioned against when they're added (see tightenGroups) —
  // otherwise a paste into a stale group lands offset.
  const doc = tightenGroups(rawDoc);

  // Paste offset: default +10/+10, or — when a cursor point is given — enough
  // to center the clipboard's top-level AABB at the cursor.
  let offset = { dx: 10, dy: 10 };
  if (centerPoint) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const id of clipTopIds) {
      const n = clipNodes[id];
      if (!n) continue;
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.width);
      maxY = Math.max(maxY, n.y + n.height);
    }
    if (Number.isFinite(minX)) {
      // Convert the world cursor point into the paste target's local space
      // (an entered group's children are positioned relative to it).
      const targetOffset = enteredGroupId
        ? getNodeWorldBounds(computeLayout(doc), enteredGroupId)
        : { x: 0, y: 0 };
      offset = {
        dx: centerPoint.x - targetOffset.x - (minX + maxX) / 2,
        dy: centerPoint.y - targetOffset.y - (minY + maxY) / 2,
      };
    }
  }

  // Clone through a mini-doc so every paste mints fresh ids. This is also what
  // detaches an external payload from the ids it was minted with elsewhere.
  const miniDoc: SceneDocument = {
    version: 2,
    width: doc.width,
    height: doc.height,
    nodes: clipNodes,
    rootChildren: clipTopIds,
  };
  const { nodes, newIds } = mutCloneNodes(miniDoc, clipTopIds, offset);

  return {
    doc: mutAddClonedNodes(doc, nodes, newIds, enteredGroupId ?? undefined),
    nodes,
    newIds,
  };
}

// ─── Store factory ────────────────────────────────────────────────────────

export function createCanvasEditorStore(
  initialDoc: SceneDocument,
  storeOpts?: { artboardFormat?: 'email'; brandContext?: BrandResolverContext | null }
) {
  const artboardOpts = storeOpts?.artboardFormat ? { format: storeOpts.artboardFormat } : undefined;
  return createStore<CanvasEditorState & CanvasEditorActions>()((set, get) => ({
    // ── Initial state ────────────────────────────────────────────────

    doc: commitRootSize(ensureArtboardRoot(autoUpgradeStackedGroups(initialDoc), artboardOpts)),
    brandContext: storeOpts?.brandContext ?? null,
    selectedIds: new Set<string>(),
    hoveredId: null,
    enteredGroupId: null,

    interactionMode: 'idle' as InteractionMode,
    activeHandle: null,
    activeRadiusHandle: null,
    dragStart: null,
    dragNodeStarts: new Map(),
    resizeStartBounds: null,
    multiResizeStarts: null,
    rotationStartAngle: 0,
    marqueeStart: null,
    marqueeRect: null,
    snapGuides: [],
    dragReorder: null,
    dragDropTarget: null,

    zoom: 1,
    panOffset: { x: 0, y: 0 },
    panStart: null,
    panOffsetStart: null,

    undoStack: [],
    redoStack: [],

    clipboardNodes: null,
    clipboardTopIds: [],
    clipboardStamp: null,

    editingTextId: null,

    croppingNodeId: null,
    croppingFillIndex: null,
    cropDragStart: null,
    cropStartTransform: null,
    cropResizeHandle: null,
    cropStartFrame: null,

    drawTool: null,
    drawingNodeId: null,
    drawOrigin: null,

    vectorEditingNodeId: null,
    vectorPaths: null,
    vectorSelectedPoints: new Set<string>(),
    vectorDragTarget: null,
    vectorDragStart: null,
    vectorDragPointStart: null,

    penPaths: null,
    penCurrentPath: null,
    penDragging: false,

    freehandPoints: null,
    vectorGridSize: 0,

    linkZonesVisible: false,
    guidesVisible: true,
    altMeasureActive: false,

    renderVersion: 0,

    // ── Init ─────────────────────────────────────────────────────────

    setDocument(doc) {
      set({
        doc: commitRootSize(ensureArtboardRoot(autoUpgradeStackedGroups(doc), artboardOpts)),
        selectedIds: new Set(),
        hoveredId: null,
        enteredGroupId: null,
        undoStack: [],
        redoStack: [],
        renderVersion: get().renderVersion + 1,
      });
    },

    setBrandContext(ctx) {
      if (get().brandContext === ctx) return;
      set({ brandContext: ctx, renderVersion: get().renderVersion + 1 });
    },

    // ── Selection ────────────────────────────────────────────────────

    select(nodeId) {
      set({
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    selectMultiple(nodeIds) {
      set({
        selectedIds: new Set(nodeIds),
        renderVersion: get().renderVersion + 1,
      });
    },

    toggleSelect(nodeId) {
      const { selectedIds } = get();
      const next = new Set(selectedIds);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      set({ selectedIds: next, renderVersion: get().renderVersion + 1 });
    },

    clearSelection() {
      set({
        selectedIds: new Set(),
        enteredGroupId: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    setHovered(hoveredId) {
      if (get().hoveredId === hoveredId) return;
      set({ hoveredId, renderVersion: get().renderVersion + 1 });
    },

    enterGroup(groupId) {
      set({
        enteredGroupId: groupId,
        selectedIds: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    exitGroup() {
      const { enteredGroupId } = get();
      if (enteredGroupId) {
        set({
          enteredGroupId: null,
          selectedIds: new Set([enteredGroupId]),
          renderVersion: get().renderVersion + 1,
        });
      }
    },

    selectAll() {
      const { doc, enteredGroupId } = get();
      const ids = enteredGroupId
        ? doc.nodes[enteredGroupId]?.children ?? []
        : doc.rootChildren;
      const selectable = ids.filter((id) => {
        const n = doc.nodes[id];
        return !!n && n.slotRole !== 'link-zone' && n.visible !== false && !n.locked;
      });
      if (selectable.length === 0) return;
      set({ selectedIds: new Set(selectable), renderVersion: get().renderVersion + 1 });
    },

    selectSibling(direction) {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const id = [...selectedIds][0];
      const node = doc.nodes[id];
      if (!node) return;
      const siblings = (node.parentId ? doc.nodes[node.parentId]?.children : doc.rootChildren) ?? [];
      const eligible = siblings.filter((sid) => {
        const n = doc.nodes[sid];
        return !!n && n.slotRole !== 'link-zone';
      });
      const idx = eligible.indexOf(id);
      if (idx < 0 || eligible.length < 2) return;
      const nextIdx = (idx + direction + eligible.length) % eligible.length;
      set({ selectedIds: new Set([eligible[nextIdx]]), renderVersion: get().renderVersion + 1 });
    },

    descendSelection() {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const id = [...selectedIds][0];
      const node = doc.nodes[id];
      if (!node || (node.type !== 'GROUP' && node.type !== 'FRAME')) return;
      const firstChild = node.children?.find((cid) => doc.nodes[cid]);
      if (!firstChild) return;
      set({
        enteredGroupId: id,
        selectedIds: new Set([firstChild]),
        renderVersion: get().renderVersion + 1,
      });
    },

    ascendSelection() {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const id = [...selectedIds][0];
      const node = doc.nodes[id];
      const parentId = node?.parentId;
      if (!parentId || !doc.nodes[parentId]) return;
      // The parent becomes selected; the "entered" context moves up to the
      // grandparent so the parent stays directly selectable (null at root).
      const grandParentId = doc.nodes[parentId]?.parentId ?? null;
      set({
        enteredGroupId: grandParentId,
        selectedIds: new Set([parentId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Drag ─────────────────────────────────────────────────────────

    startDrag(scenePoint, opts) {
      const { selectedIds, doc } = get();
      const starts = new Map<
        string,
        { x: number; y: number; aabb: { x: number; y: number; width: number; height: number } }
      >();
      for (const id of selectedIds) {
        const node = doc.nodes[id];
        if (node) {
          starts.set(id, { x: node.x, y: node.y, aabb: getNodeAABB(doc, id) });
        }
      }

      // Detect auto-layout reorder: single selection whose parent has
      // layoutMode VERTICAL or HORIZONTAL. Free translate is meaningless inside
      // an auto-layout container because the layout pass overwrites x/y, so
      // we switch the drag into a children-array reorder.
      let reorder: CanvasEditorState['dragReorder'] = null;
      if (selectedIds.size === 1) {
        const draggedId = [...selectedIds][0];
        const node = doc.nodes[draggedId];
        // Figma-style absolutely-positioned children float over the flow —
        // they free-drag; the reorder gesture is only for flow children.
        if (node?.parentId && node.layoutPositioning !== 'ABSOLUTE') {
          const parent = doc.nodes[node.parentId];
          const mode = parent?.layoutMode;
          if ((mode === 'VERTICAL' || mode === 'HORIZONTAL') && parent.children) {
            const fromIndex = parent.children.indexOf(draggedId);
            if (fromIndex >= 0) {
              reorder = {
                parentId: parent.id,
                draggedId,
                layoutMode: mode,
                fromIndex,
                toIndex: fromIndex,
                deltaX: 0,
                deltaY: 0,
              };
            }
          }
          // Absolute email artboard: top-level bands reorder as a vertical
          // stack even without auto-layout (emails are a stack; see
          // canvas-band-restack.ts). Indices are positions in the y-sorted
          // band stack.
          if (
            !reorder &&
            parent &&
            isAbsoluteArtboard(doc, parent) &&
            (node.type === 'FRAME' || node.type === 'GROUP') &&
            node.slotRole !== 'link-zone'
          ) {
            const stack = bandStack(doc, parent.id);
            const fromIndex = stack.indexOf(draggedId);
            if (fromIndex >= 0 && stack.length > 1) {
              reorder = {
                parentId: parent.id,
                draggedId,
                layoutMode: 'VERTICAL',
                fromIndex,
                toIndex: fromIndex,
                deltaX: 0,
                deltaY: 0,
                absolute: true,
              };
            }
          }
        }
      }

      // Alt+drag duplicate pushes its own single history entry for the whole
      // gesture, then starts the drag with skipHistory so we don't double-push.
      if (!opts?.skipHistory) get().pushHistory();
      set({
        interactionMode: 'dragging',
        dragStart: scenePoint,
        dragNodeStarts: starts,
        snapGuides: [],
        dragReorder: reorder,
        dragDropTarget: null,
      });
    },

    beginAltDragDuplicate(scenePoint) {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) {
        get().startDrag(scenePoint);
        return;
      }
      // Single history entry for the whole duplicate+drag gesture: snapshot the
      // pre-duplicate doc now, then clone/drag without pushing again.
      get().pushHistory();
      // Clone in place (no offset) — the copies then follow the cursor.
      const { doc: nextDoc, newIds } = mutDuplicateAsSiblings(doc, [...selectedIds], {
        dx: 0,
        dy: 0,
      });
      set({
        doc: nextDoc,
        selectedIds: new Set(newIds),
        renderVersion: get().renderVersion + 1,
      });
      get().startDrag(scenePoint, { skipHistory: true });
    },

    updateDrag(scenePoint, shiftKey, disableSnap) {
      const { dragStart, dragNodeStarts, doc, selectedIds, dragReorder } = get();
      if (!dragStart) return;

      // Auto-layout reorder branch: track target index + cursor delta in
      // transient state. Renderer paints the ghost preview; the doc is only
      // mutated on endDrag.
      if (dragReorder) {
        // Plain drag of a flow child is reorder-only (Figma parity): it never
        // auto-floats out of the stack. Free placement is an explicit choice —
        // float the element (toggleSelectedFloating) or turn off the section's
        // auto-layout (setSelectedAutoLayout / Shift+A). Keeping drag = reorder
        // is what makes dragging predictable instead of "it randomly broke".
        const laidOut = computeLayout(doc);
        const parent = laidOut.nodes[dragReorder.parentId];
        if (!parent?.children?.length) return;

        const isV = dragReorder.layoutMode === 'VERTICAL';
        const cursor = isV ? scenePoint.y : scenePoint.x;

        // Absolute band reorder works over the y-sorted stack (children array
        // order may not match visual order in hand-placed scenes).
        const ordered = dragReorder.absolute
          ? bandStack(laidOut, dragReorder.parentId)
          : parent.children;
        const otherIds = ordered.filter((id) => id !== dragReorder.draggedId);
        let toIndex = otherIds.length;
        for (let i = 0; i < otherIds.length; i++) {
          const aabb = getNodeAABB(laidOut, otherIds[i]);
          const center = isV ? aabb.y + aabb.height / 2 : aabb.x + aabb.width / 2;
          if (cursor < center) {
            toIndex = i;
            break;
          }
        }

        set({
          dragReorder: {
            ...dragReorder,
            toIndex,
            deltaX: scenePoint.x - dragStart.x,
            deltaY: scenePoint.y - dragStart.y,
          },
          snapGuides: [],
          renderVersion: get().renderVersion + 1,
        });
        return;
      }

      let dx = scenePoint.x - dragStart.x;
      let dy = scenePoint.y - dragStart.y;

      // Shift: lock movement to the dominant axis (Figma parity). The
      // perpendicular snap delta is zeroed below so snapping can't reintroduce
      // off-axis motion.
      let lockAxis: 'x' | 'y' | null = null;
      if (shiftKey) {
        if (Math.abs(dx) >= Math.abs(dy)) {
          dy = 0;
          lockAxis = 'x';
        } else {
          dx = 0;
          lockAxis = 'y';
        }
      }

      // Link zones: disable free dragging entirely -- use resize handles instead.
      // Moving a zone without adjusting neighbors would break 100% coverage.
      const isLinkZone = [...selectedIds].some((id) => {
        const node = doc.nodes[id];
        return node?.parentId && doc.nodes[node.parentId]?.slotRole === 'link-zone';
      });
      if (isLinkZone) return;

      // Compute proposed bounds in world space for snap.
      // Use cached start AABB (from startDrag) + drag delta for correct world-space bounds.
      // This handles GROUP nodes whose width/height are 0 (bounds derived from children).
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      const patches = new Map<string, Partial<SceneNode>>();

      for (const [id, start] of dragNodeStarts) {
        const node = doc.nodes[id];
        if (!node) continue;

        // Proposed world bounds = start AABB + drag delta
        const visualX = start.aabb.x + (isLinkZone ? 0 : dx);
        const visualY = start.aabb.y + dy;
        const visualW = start.aabb.width;
        const visualH = start.aabb.height;
        minX = Math.min(minX, visualX);
        minY = Math.min(minY, visualY);
        maxX = Math.max(maxX, visualX + visualW);
        maxY = Math.max(maxY, visualY + visualH);
      }

      // Snap (hold Cmd/Ctrl to disable snapping, like Figma — Alt is now
      // reserved for duplicate-drag).
      const snapResult = disableSnap
        ? { adjusted: { x: minX, y: minY }, guides: [] as SnapGuide[] }
        : computeSnap(
            doc,
            selectedIds,
            {
              x: minX,
              y: minY,
              width: maxX - minX,
              height: maxY - minY,
            },
            { includeGuides: get().guidesVisible },
          );
      let snapDx = isLinkZone ? 0 : snapResult.adjusted.x - minX;
      let snapDy = snapResult.adjusted.y - minY;
      // Keep the axis lock intact: drop the snap nudge on the locked-out axis.
      if (lockAxis === 'x') snapDy = 0;
      if (lockAxis === 'y') snapDx = 0;

      for (const [id, start] of dragNodeStarts) {
        patches.set(id, {
          x: isLinkZone ? 0 : start.x + dx + snapDx,
          y: start.y + dy + snapDy,
        });
      }

      const nextDoc = mutUpdateNodes(doc, patches);

      // Nest target: when free-dragging a single node, highlight the FRAME/GROUP
      // under the cursor that it would reparent into on release. Skip link zones
      // and no-op (already the node's parent) targets.
      let dropTarget: string | null = null;
      if (!isLinkZone && selectedIds.size === 1) {
        const draggedId = [...selectedIds][0];
        const exclude = collectSubtree(nextDoc, draggedId);
        // Probe the node's whole footprint, not just the cursor: a node
        // straddling two stacked sections must nest into the latest-painted
        // one it overlaps or the overlap ends up hidden behind it.
        const candidate = findDropContainerForNode(nextDoc, draggedId, exclude);
        const currentParent = nextDoc.nodes[draggedId]?.parentId ?? null;
        if (candidate && candidate !== currentParent) dropTarget = candidate;
      }

      set({
        doc: nextDoc,
        snapGuides: snapResult.guides,
        dragDropTarget: dropTarget,
        renderVersion: get().renderVersion + 1,
      });
    },

    endDrag() {
      const { dragReorder, dragDropTarget, selectedIds, doc } = get();
      let nextDoc = doc;

      if (dragReorder?.absolute && dragReorder.toIndex !== dragReorder.fromIndex) {
        // Absolute band restack: reassign y positions (slot gaps preserved)
        // and sync the children array to the new visual order.
        const patches = restackBands(
          doc,
          dragReorder.parentId,
          dragReorder.draggedId,
          dragReorder.toIndex
        );
        nextDoc = mutUpdateNodes(doc, patches);
        const parent = nextDoc.nodes[dragReorder.parentId];
        if (parent) {
          nextDoc = {
            ...nextDoc,
            nodes: {
              ...nextDoc.nodes,
              [parent.id]: {
                ...parent,
                children: reorderChildrenToStack(
                  parent,
                  doc,
                  dragReorder.draggedId,
                  dragReorder.toIndex
                ),
              },
            },
          };
        }
      } else if (dragReorder && dragReorder.toIndex !== dragReorder.fromIndex) {
        nextDoc = mutMoveNodeToIndex(doc, dragReorder.draggedId, dragReorder.toIndex);
      } else if (dragDropTarget && selectedIds.size === 1) {
        // Nest the dragged node into the highlighted container, preserving its
        // on-screen position. Auto-layout targets would pull the node into the
        // flow and shove its new siblings around — instead mark it ABSOLUTE
        // (Figma's absolute positioning) so it floats exactly where dropped.
        const draggedId = [...selectedIds][0];
        const target = doc.nodes[dragDropTarget];
        if (
          target &&
          draggedId !== dragDropTarget &&
          target.id !== doc.nodes[draggedId]?.parentId
        ) {
          // Re-fit groups first: the offsets below are read from the laid-out
          // doc and written back to this one, so both have to agree on where a
          // group's origin sits (see tightenGroups). Safe here — the free drag
          // is over, so no start position depends on the old local coords.
          const tight = tightenGroups(doc);
          const worldBefore = getNodeWorldBounds(computeLayout(tight), draggedId);
          const insertIndex = target.children?.length ?? 0;
          let d = mutReparentNode(tight, draggedId, dragDropTarget, insertIndex);
          const targetAutoLayout =
            target.layoutMode === 'VERTICAL' || target.layoutMode === 'HORIZONTAL';
          if (targetAutoLayout) {
            // Mark ABSOLUTE before measuring: a flow insert would reflow the
            // target (and its HUG ancestors) and skew the offset below.
            d = mutUpdateNode(d, draggedId, { layoutPositioning: 'ABSOLUTE' as const });
          }
          // Offset from the laid-out doc: the target's own position may be
          // flow-computed when it sits inside another auto-layout container.
          const parentOffset = getParentWorldOffset(computeLayout(d), draggedId);
          d = mutUpdateNode(d, draggedId, {
            x: worldBefore.x - parentOffset.x,
            y: worldBefore.y - parentOffset.y,
          });
          nextDoc = d;
        }
      }

      set({
        doc: commitRootSize(nextDoc),
        interactionMode: 'idle',
        dragStart: null,
        dragNodeStarts: new Map(),
        snapGuides: [],
        dragReorder: null,
        dragDropTarget: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Resize ───────────────────────────────────────────────────────

    startResize(handle, scenePoint) {
      const { selectedIds, doc } = get();
      if (selectedIds.size === 0 || handle === 'rotate') return;

      // ── Multi-select bounding-box resize ──────────────────────────────
      // 2+ nodes: capture the combined selection AABB plus each top-level
      // node's start geometry, then scale everything inside the box on drag.
      if (selectedIds.size > 1) {
        const laidOut = computeLayout(doc);
        const topIds = topmostSelectedIds(doc, selectedIds).filter((id) => {
          const n = doc.nodes[id];
          if (!n) return false;
          // The page artboard is the document, not resizable content; link
          // zones own neighbor-aware sizing that a plain scale would corrupt.
          if (n.parentId == null && n.pluginData?.['isArtboard'] === true) return false;
          if (n.parentId && doc.nodes[n.parentId]?.slotRole === 'link-zone') return false;
          return true;
        });
        const aabb = getSelectionAABB(laidOut, topIds);
        if (!aabb || topIds.length === 0) return;

        const starts = new Map<
          string,
          { x: number; y: number; width: number; height: number; parentOffset: Point }
        >();
        for (const id of topIds) {
          const wb = getNodeWorldBounds(laidOut, id);
          starts.set(id, {
            x: wb.x,
            y: wb.y,
            width: wb.width,
            height: wb.height,
            parentOffset: getParentWorldOffset(laidOut, id),
          });
        }

        get().pushHistory();
        set({
          interactionMode: 'resizing',
          activeHandle: handle,
          dragStart: scenePoint,
          resizeStartBounds: { ...aabb, rotation: 0 },
          multiResizeStarts: starts,
        });
        return;
      }

      const nodeId = [...selectedIds][0];
      // Defense in depth: hit-testing already skips disabled handles, but a
      // programmatic call must not resize a HUG axis (it would silently flip
      // it to FIXED and kill auto-grow).
      if (!getEnabledHandles(doc.nodes[nodeId]).includes(handle)) return;
      // Capture bounds from the laid-out doc so the resize math is anchored
      // to the same coords the user grabbed visually.
      const bounds = getNodeWorldBounds(computeLayout(doc), nodeId);

      get().pushHistory();
      set({
        interactionMode: 'resizing',
        activeHandle: handle,
        dragStart: scenePoint,
        resizeStartBounds: bounds,
        multiResizeStarts: null,
      });
    },

    updateResize(scenePoint, shiftKey, altKey) {
      const { activeHandle, dragStart, resizeStartBounds, multiResizeStarts, selectedIds } = get();
      if (!activeHandle || !dragStart || !resizeStartBounds || activeHandle === 'rotate') return;

      // Re-fit groups before converting world -> local. computeLayout re-fits
      // them on every pass, so a raw store doc and its laid-out twin can
      // disagree about where a group's origin sits — and the parent offset
      // below is read from the laid-out doc while the patch is written to this
      // one. Re-fitting never moves anything on screen, so the drag can't feel
      // it. See tightenGroups.
      const doc = tightenGroups(get().doc);

      // ── Multi-select bounding-box resize ──────────────────────────────
      // Resize the combined AABB (Shift = aspect lock, Alt = from center via
      // computeResize), then scale every node proportionally inside it. Font
      // sizes / stroke weights are left as-is — this is Resize, not Scale.
      if (multiResizeStarts) {
        const startAABB = resizeStartBounds;
        const newAABB = computeResize(
          activeHandle,
          startAABB,
          dragStart,
          scenePoint,
          shiftKey,
          !!altKey,
        );
        const handleStr = activeHandle as string;
        const changedWidth = handleStr.includes('e') || handleStr.includes('w');
        const changedHeight = handleStr.includes('n') || handleStr.includes('s');
        const patches = new Map<string, Partial<SceneNode>>();
        for (const [id, s] of multiResizeStarts) {
          const node = doc.nodes[id];
          if (!node) continue;
          const scaled = scaleNodeBoundsInAABB(s, startAABB, newAABB);
          const patch: Partial<SceneNode> = {
            x: scaled.x - s.parentOffset.x,
            y: scaled.y - s.parentOffset.y,
            width: scaled.width,
            height: scaled.height,
          };
          // Mirror single-node resize: an auto-layout container hugging on a
          // scaled axis must flip to FIXED or the layout pass overwrites the
          // new size on the next frame.
          if (node.layoutMode === 'VERTICAL' || node.layoutMode === 'HORIZONTAL') {
            if (changedWidth && node.layoutSizingHorizontal === 'HUG') {
              patch.layoutSizingHorizontal = 'FIXED';
            }
            if (changedHeight && node.layoutSizingVertical === 'HUG') {
              patch.layoutSizingVertical = 'FIXED';
            }
          }
          patches.set(id, patch);
        }
        set({ doc: mutUpdateNodes(doc, patches), renderVersion: get().renderVersion + 1 });
        return;
      }

      const nodeId = [...selectedIds][0];
      const node = doc.nodes[nodeId];

      const isLinkZone = node?.parentId && doc.nodes[node.parentId]?.slotRole === 'link-zone';

      const result = computeResize(
        activeHandle,
        resizeStartBounds,
        dragStart,
        scenePoint,
        shiftKey,
        // Alt = resize symmetrically about the center. Link zones keep their
        // neighbor-aware edge resize (their own math re-derives the frame).
        !!altKey && !isLinkZone
      );

      // Convert world-space result back to local (parent-relative) coordinates.
      // Use the laid-out doc so the parent offset matches the bounds captured
      // in startResize (parent positions are stable during a child resize).
      const parentOffset = getParentWorldOffset(computeLayout(doc), nodeId);
      const nodeX = result.x - parentOffset.x;
      const nodeY = result.y - parentOffset.y;
      const nodeW = result.width;
      const nodeH = result.height;

      if (!isLinkZone) {
        // If the node is an auto-layout container with HUG sizing on the
        // dragged axis, flip it to FIXED — otherwise computeLayout will
        // overwrite the new size on the next pass and the drag won't stick.
        const handleStr = activeHandle as string;
        const changedWidth = handleStr.includes('e') || handleStr.includes('w');
        const changedHeight = handleStr.includes('n') || handleStr.includes('s');
        const patch: Partial<SceneNode> = { x: nodeX, y: nodeY, width: nodeW, height: nodeH };
        if (node?.layoutMode === 'VERTICAL' || node?.layoutMode === 'HORIZONTAL') {
          if (changedWidth && node.layoutSizingHorizontal === 'HUG') {
            patch.layoutSizingHorizontal = 'FIXED';
          }
          if (changedHeight && node.layoutSizingVertical === 'HUG') {
            patch.layoutSizingVertical = 'FIXED';
          }
        }
        // Figma-style implicit auto-resize transitions: manually setting a text
        // box's width demotes it a step (AUTO_WIDTH -> AUTO_HEIGHT), and setting
        // its height pins it fully (-> FIXED). Only fires when the field is
        // already present, so untagged text is unaffected.
        if (node?.type === 'TEXT' && node.textAutoResize) {
          if (changedHeight) {
            patch.textAutoResize = 'FIXED';
          } else if (changedWidth && node.textAutoResize === 'AUTO_WIDTH') {
            patch.textAutoResize = 'AUTO_HEIGHT';
          }
        }
        const nextDoc = mutUpdateNode(doc, nodeId, patch);
        set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
        return;
      }

      // ── Link zone neighbor-aware resize ──────────────────────────────
      const MIN_ZONE = 20;
      const neighbors = findZoneNeighbors(nodeId, doc);
      const patches = new Map<string, Partial<SceneNode>>();

      const oldY = node.y;
      const oldH = node.height;
      const oldRight = node.x + node.width;
      let newY = nodeY;
      let newH = nodeH;
      let newX = node.x;
      let newW = node.width;

      // Determine which edge(s) moved
      const handle = activeHandle as string;
      const movedTop = handle.includes('n');
      const movedBottom = handle.includes('s');
      const movedLeft = handle === 'w' || handle === 'nw' || handle === 'sw';
      const movedRight = handle === 'e' || handle === 'ne' || handle === 'se';

      // Vertical: top edge moved → adjust top neighbors
      if (movedTop && !movedBottom) {
        const topDelta = newY - oldY; // negative = grew up, positive = shrunk
        for (const nb of neighbors.top) {
          const nbNewH = nb.height + topDelta; // inverse: if zone grew up, neighbor shrinks
          if (nbNewH < MIN_ZONE) {
            // Clamp: neighbor can't go below minimum
            const clampedDelta = nb.height - MIN_ZONE;
            newY = oldY - clampedDelta;
            newH = oldH + clampedDelta;
            patches.set(nb.id, { height: MIN_ZONE });
          } else {
            patches.set(nb.id, { height: nbNewH });
          }
        }
        // If no top neighbors, clamp to canvas top (y=0)
        if (neighbors.top.length === 0) {
          newY = Math.max(0, newY);
          newH = oldY + oldH - newY;
        }
      }

      // Vertical: bottom edge moved → adjust bottom neighbors
      if (movedBottom && !movedTop) {
        const bottomDelta = newY + newH - (oldY + oldH); // positive = grew down
        for (const nb of neighbors.bottom) {
          const nbNewY = nb.y + bottomDelta;
          const nbNewH = nb.height - bottomDelta;
          if (nbNewH < MIN_ZONE) {
            const clampedDelta = nb.height - MIN_ZONE;
            newH = oldH + clampedDelta;
            patches.set(nb.id, { y: nb.y + clampedDelta, height: MIN_ZONE });
          } else {
            patches.set(nb.id, { y: nbNewY, height: nbNewH });
          }
        }
        if (neighbors.bottom.length === 0) {
          newH = Math.min(doc.height - newY, newH);
        }
      }

      // Horizontal: left edge moved → adjust left neighbors
      if (movedLeft) {
        const leftDelta = newX - node.x;
        for (const nb of neighbors.left) {
          const nbNewW = nb.width + leftDelta;
          if (nbNewW < MIN_ZONE) {
            const clampedDelta = nb.width - MIN_ZONE;
            newX = node.x - clampedDelta;
            newW = node.width + clampedDelta;
            patches.set(nb.id, { width: MIN_ZONE });
          } else {
            patches.set(nb.id, { width: nbNewW });
          }
        }
        if (neighbors.left.length === 0) {
          newX = Math.max(0, newX);
          newW = oldRight - newX;
        }
      }

      // Horizontal: right edge moved → adjust right neighbors
      if (movedRight) {
        const rightDelta = newX + newW - oldRight;
        for (const nb of neighbors.right) {
          const nbNewX = nb.x + rightDelta;
          const nbNewW = nb.width - rightDelta;
          if (nbNewW < MIN_ZONE) {
            const clampedDelta = nb.width - MIN_ZONE;
            newW = node.width + clampedDelta;
            patches.set(nb.id, { x: nb.x + clampedDelta, width: MIN_ZONE });
          } else {
            patches.set(nb.id, { x: nbNewX, width: nbNewW });
          }
        }
        if (neighbors.right.length === 0) {
          newW = Math.min(doc.width - newX, newW);
        }
      }

      // Enforce minimum on the resized zone itself
      newH = Math.max(MIN_ZONE, newH);
      newW = Math.max(MIN_ZONE, newW);

      patches.set(nodeId, { x: newX, y: newY, width: newW, height: newH });

      const nextDoc = mutUpdateNodes(doc, patches);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    endResize() {
      set({
        doc: commitRootSize(get().doc),
        interactionMode: 'idle',
        activeHandle: null,
        dragStart: null,
        resizeStartBounds: null,
        multiResizeStarts: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Rotate ───────────────────────────────────────────────────────

    startRotate(scenePoint) {
      const { selectedIds, doc } = get();
      if (selectedIds.size !== 1) return;

      const nodeId = [...selectedIds][0];
      const bounds = getNodeWorldBounds(doc, nodeId);
      const center = { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
      const startAngle = computeStartAngle(center, scenePoint, bounds.rotation);

      get().pushHistory();
      set({
        interactionMode: 'rotating',
        activeHandle: 'rotate',
        dragStart: scenePoint,
        resizeStartBounds: bounds,
        rotationStartAngle: startAngle,
      });
    },

    updateRotate(scenePoint, shiftKey) {
      const { resizeStartBounds, rotationStartAngle, selectedIds, doc } = get();
      if (!resizeStartBounds) return;

      const nodeId = [...selectedIds][0];
      const center = {
        x: resizeStartBounds.x + resizeStartBounds.width / 2,
        y: resizeStartBounds.y + resizeStartBounds.height / 2,
      };

      const newRotation = computeRotation(center, scenePoint, rotationStartAngle, shiftKey);
      const nextDoc = mutUpdateNode(doc, nodeId, { rotation: newRotation });

      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    endRotate() {
      set({
        interactionMode: 'idle',
        activeHandle: null,
        dragStart: null,
        resizeStartBounds: null,
        rotationStartAngle: 0,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Corner radius ────────────────────────────────────────────────

    startRadiusDrag(handle, scenePoint) {
      const { selectedIds, doc } = get();
      if (selectedIds.size !== 1) return;
      const nodeId = [...selectedIds][0];
      // Anchor the drag math to the laid-out bounds the user sees, same as
      // startResize (auto-layout shifts child x/y away from authored coords).
      const bounds = getNodeWorldBounds(computeLayout(doc), nodeId);

      get().pushHistory();
      set({
        interactionMode: 'radiusDragging',
        activeRadiusHandle: handle,
        dragStart: scenePoint,
        resizeStartBounds: bounds,
      });
    },

    updateRadiusDrag(scenePoint, altKey) {
      const { activeRadiusHandle, resizeStartBounds, selectedIds, doc } = get();
      if (!activeRadiusHandle || !resizeStartBounds) return;
      const nodeId = [...selectedIds][0];
      const node = doc.nodes[nodeId];
      if (!node) return;

      const r = computeCornerRadiusFromPoint(activeRadiusHandle, resizeStartBounds, scenePoint);

      // Figma semantics: dragging any circle sets a uniform radius; holding
      // Alt edits just that corner (per-corner radii win over cornerRadius in
      // the renderer, so the uniform path clears them).
      let patch: Partial<SceneNode>;
      if (altKey) {
        const radii = nodeCornerRadii(node);
        const idx = { 'radius-tl': 0, 'radius-tr': 1, 'radius-br': 2, 'radius-bl': 3 }[
          activeRadiusHandle
        ];
        radii[idx] = r;
        patch = { cornerRadii: radii, cornerRadius: undefined };
      } else {
        patch = { cornerRadius: r, cornerRadii: undefined };
      }

      set({ doc: mutUpdateNode(doc, nodeId, patch), renderVersion: get().renderVersion + 1 });
    },

    endRadiusDrag() {
      set({
        interactionMode: 'idle',
        activeRadiusHandle: null,
        dragStart: null,
        resizeStartBounds: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Marquee ──────────────────────────────────────────────────────

    startMarquee(scenePoint) {
      set({
        interactionMode: 'marquee',
        marqueeStart: scenePoint,
        marqueeRect: { x: scenePoint.x, y: scenePoint.y, w: 0, h: 0 },
        renderVersion: get().renderVersion + 1,
      });
    },

    updateMarquee(scenePoint) {
      const { marqueeStart } = get();
      if (!marqueeStart) return;

      set({
        marqueeRect: {
          x: marqueeStart.x,
          y: marqueeStart.y,
          w: scenePoint.x - marqueeStart.x,
          h: scenePoint.y - marqueeStart.y,
        },
        renderVersion: get().renderVersion + 1,
      });
    },

    endMarquee() {
      const { marqueeRect, doc, enteredGroupId, linkZonesVisible } = get();
      let newSelection = new Set<string>();

      if (marqueeRect) {
        // Normalize negative w/h
        const rect = {
          x: marqueeRect.w < 0 ? marqueeRect.x + marqueeRect.w : marqueeRect.x,
          y: marqueeRect.h < 0 ? marqueeRect.y + marqueeRect.h : marqueeRect.y,
          width: Math.abs(marqueeRect.w),
          height: Math.abs(marqueeRect.h),
        };

        if (rect.width > 2 || rect.height > 2) {
          const ids = marqueeSelect(doc, rect, enteredGroupId, linkZonesVisible);
          newSelection = new Set(ids);
        }
      }

      set({
        interactionMode: 'idle',
        marqueeStart: null,
        marqueeRect: null,
        selectedIds: newSelection,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Pan ──────────────────────────────────────────────────────────

    startPan(screenPoint) {
      const { panOffset } = get();
      set({
        interactionMode: 'panning',
        panStart: screenPoint,
        panOffsetStart: { ...panOffset },
      });
    },

    updatePan(screenPoint) {
      const { panStart, panOffsetStart } = get();
      if (!panStart || !panOffsetStart) return;

      set({
        panOffset: {
          x: panOffsetStart.x + (screenPoint.x - panStart.x),
          y: panOffsetStart.y + (screenPoint.y - panStart.y),
        },
        renderVersion: get().renderVersion + 1,
      });
    },

    endPan() {
      set({
        interactionMode: 'idle',
        panStart: null,
        panOffsetStart: null,
      });
    },

    // ── Draw tool ────────────────────────────────────────────────────

    setDrawTool(tool) {
      const current = get().drawTool;
      set({
        drawTool: tool === current ? null : tool,
        selectedIds: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    startDrawing(scenePoint) {
      const { drawTool, doc } = get();
      if (!drawTool || drawTool === 'PEN' || drawTool === 'FREEHAND') return;

      get().pushHistory();

      const nodeId = generateNodeId();
      const baseProps: SceneNode = {
        id: nodeId,
        type: drawTool,
        parentId: null,
        x: scenePoint.x,
        y: scenePoint.y,
        width: 0,
        height: 0,
      };

      // Apply type-specific defaults
      switch (drawTool) {
        case 'RECTANGLE':
          baseProps.fills = [{ type: 'SOLID', color: '#D1D5DB' }];
          break;
        case 'ELLIPSE':
          baseProps.fills = [{ type: 'SOLID', color: '#D1D5DB' }];
          break;
        case 'STAR':
          baseProps.fills = [{ type: 'SOLID', color: '#fbbf24' }];
          baseProps.sides = 5;
          baseProps.innerRadius = 0.4;
          break;
        case 'POLYGON':
          baseProps.fills = [{ type: 'SOLID', color: '#D1D5DB' }];
          baseProps.sides = 6;
          break;
        case 'TRAPEZOID':
          baseProps.fills = [{ type: 'SOLID', color: '#1f2c4a' }];
          break;
        case 'SEAL':
          baseProps.fills = [{ type: 'SOLID', color: '#DC2626' }];
          baseProps.sides = 16;
          baseProps.innerRadius = 0.85;
          baseProps.bumpStyle = 'sharp';
          break;
        case 'LINE':
          baseProps.points = [0, 0, 0, 0];
          baseProps.strokes = [{ type: 'SOLID', color: '#000000' }];
          baseProps.strokeWeight = 2;
          break;
      }

      const nextDoc = mutAddNode(doc, baseProps);
      set({
        doc: nextDoc,
        interactionMode: 'drawing',
        drawingNodeId: nodeId,
        drawOrigin: scenePoint,
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    updateDrawing(scenePoint, shiftKey) {
      const { drawOrigin, drawingNodeId, doc, drawTool } = get();
      if (!drawOrigin || !drawingNodeId) return;

      let dx = scenePoint.x - drawOrigin.x;
      let dy = scenePoint.y - drawOrigin.y;

      if (drawTool === 'LINE') {
        // Line: update endpoint
        if (shiftKey) {
          // Constrain to 0/45/90 degree angles
          const angle = Math.atan2(dy, dx);
          const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
          const dist = Math.sqrt(dx * dx + dy * dy);
          dx = Math.cos(snapped) * dist;
          dy = Math.sin(snapped) * dist;
        }
        const x = Math.min(drawOrigin.x, drawOrigin.x + dx);
        const y = Math.min(drawOrigin.y, drawOrigin.y + dy);
        const w = Math.abs(dx);
        const h = Math.abs(dy);
        // Points relative to node origin
        const p0x = drawOrigin.x - x;
        const p0y = drawOrigin.y - y;
        const p1x = drawOrigin.x + dx - x;
        const p1y = drawOrigin.y + dy - y;

        const nextDoc = mutUpdateNode(doc, drawingNodeId, {
          x,
          y,
          width: w || 1,
          height: h || 1,
          points: [p0x, p0y, p1x, p1y],
        });
        set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
        return;
      }

      // Shift constrains to square/circle
      if (shiftKey) {
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        dx = Math.sign(dx || 1) * size;
        dy = Math.sign(dy || 1) * size;
      }

      const x = dx >= 0 ? drawOrigin.x : drawOrigin.x + dx;
      const y = dy >= 0 ? drawOrigin.y : drawOrigin.y + dy;
      const w = Math.abs(dx);
      const h = Math.abs(dy);

      const nextDoc = mutUpdateNode(doc, drawingNodeId, { x, y, width: w, height: h });
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    endDrawing() {
      const { drawingNodeId, drawOrigin, doc, drawTool } = get();
      if (!drawingNodeId || !drawOrigin) {
        set({ interactionMode: 'idle', drawTool: null, drawingNodeId: null, drawOrigin: null });
        return;
      }

      const node = doc.nodes[drawingNodeId];
      const tooSmall = node && node.width < 3 && node.height < 3;

      if (tooSmall) {
        // Click without drag: place default-sized shape centered at click point
        const defaults: Record<string, { w: number; h: number }> = {
          RECTANGLE: { w: 100, h: 100 },
          ELLIPSE: { w: 100, h: 100 },
          STAR: { w: 100, h: 100 },
          POLYGON: { w: 100, h: 100 },
          TRAPEZOID: { w: 200, h: 100 },
          SEAL: { w: 140, h: 140 },
          LINE: { w: 200, h: 0 },
        };
        const d = defaults[drawTool || 'RECTANGLE'] || { w: 100, h: 100 };
        const patch: Partial<SceneNode> = {
          x: drawOrigin.x - d.w / 2,
          y: drawOrigin.y - d.h / 2,
          width: d.w,
          height: d.h,
        };
        if (drawTool === 'LINE') {
          patch.points = [0, 0, d.w, 0];
          patch.x = drawOrigin.x - d.w / 2;
          patch.y = drawOrigin.y;
          patch.height = 1;
        }
        if (drawTool === 'TRAPEZOID') {
          patch.skew = Math.round(d.w * 0.2);
        }
        const nextDoc = mutUpdateNode(doc, drawingNodeId, patch);
        set({
          doc: nextDoc,
          interactionMode: 'idle',
          drawTool: null,
          drawingNodeId: null,
          drawOrigin: null,
          renderVersion: get().renderVersion + 1,
        });
        return;
      }

      if (drawTool === 'TRAPEZOID' && node) {
        const skewed = mutUpdateNode(doc, drawingNodeId, {
          skew: Math.round(node.width * 0.2),
        });
        set({ doc: skewed, renderVersion: get().renderVersion + 1 });
      }

      set({
        interactionMode: 'idle',
        drawTool: null,
        drawingNodeId: null,
        drawOrigin: null,
      });
    },

    cancelDrawing() {
      const { drawingNodeId, doc } = get();
      if (drawingNodeId) {
        const nextDoc = mutRemoveNodes(doc, [drawingNodeId]);
        set({ doc: nextDoc });
      }
      // Pop the history entry we pushed in startDrawing
      const { undoStack } = get();
      if (undoStack.length > 0) {
        const prev = undoStack[undoStack.length - 1];
        set({
          doc: prev,
          undoStack: undoStack.slice(0, -1),
        });
      }
      set({
        interactionMode: 'idle',
        drawTool: null,
        drawingNodeId: null,
        drawOrigin: null,
        selectedIds: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Document mutations ───────────────────────────────────────────

    updateNodeProp(nodeId, patch) {
      const { doc } = get();
      get().pushHistory();
      const textKeys = [
        'text',
        'fontSize',
        'fontFamily',
        'fontWeight',
        'fontStyle',
        'textAlign',
        'lineHeight',
        'letterSpacing',
        'textTransform',
        'width',
      ];
      if (textKeys.some((k) => k in patch)) {
        invalidateOutlineCache(nodeId);
      }
      const nextDoc = mutUpdateNode(doc, nodeId, patch);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    applyNodePatches(patches) {
      if (patches.length === 0) return;
      const { doc } = get();
      get().pushHistory();
      const textKeys = [
        'text',
        'fontSize',
        'fontFamily',
        'fontWeight',
        'fontStyle',
        'textAlign',
        'lineHeight',
        'letterSpacing',
        'textTransform',
        'width',
      ];
      const map = new Map<string, Partial<SceneNode>>();
      for (const p of patches) {
        map.set(p.nodeId, { ...(map.get(p.nodeId) ?? {}), ...p.props });
        if (textKeys.some((k) => k in p.props)) invalidateOutlineCache(p.nodeId);
      }
      // Re-run layout so auto-layout blocks reflow around changed text sizes.
      const nextDoc = commitRootSize(mutUpdateNodes(doc, map));
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    tryUpgradeFrameToAutoLayout(frameId) {
      const { doc } = get();
      const node = doc.nodes[frameId];
      if (!node) return false;
      const patch = deriveStackedUpgrade(node, doc, { force: true });
      if (!patch) return false;
      get().pushHistory();
      const nextDoc = commitRootSize(mutUpdateNode(doc, frameId, patch));
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
      return true;
    },

    addNewNode(node, parentId) {
      const { doc, selectedIds } = get();
      get().pushHistory();

      // Figma-style placement: with no explicit parent, a new element joins the
      // Section/frame it lands in (selected frame, else the frame under its
      // center) instead of floating at the page root. Full-width nodes
      // (backgrounds, bands) stay at the page level. mutAddNode's default still
      // handles the page-artboard case, so only sub-frame nesting is rerouted.
      let target = parentId;
      let placed = node;
      if (!target && node.width < (doc.width || 600) * 0.9) {
        const center = { x: node.x + node.width / 2, y: node.y + node.height / 2 };
        const resolved = resolveInsertParent(doc, selectedIds, center);
        if (resolved) {
          const pBox = getNodeAABB(computeLayout(doc), resolved);
          const inside =
            center.x >= pBox.x &&
            center.x <= pBox.x + pBox.width &&
            center.y >= pBox.y &&
            center.y <= pBox.y + pBox.height;
          // Honor an in-bounds insertion point; otherwise center within the
          // frame so the element lands visibly inside it.
          const localX = inside ? node.x - pBox.x : Math.round((pBox.width - node.width) / 2);
          const localY = inside ? node.y - pBox.y : Math.round((pBox.height - node.height) / 2);
          const parent = doc.nodes[resolved];
          const parentAutoLayout =
            parent?.layoutMode === 'VERTICAL' || parent?.layoutMode === 'HORIZONTAL';
          placed = {
            ...node,
            parentId: resolved,
            x: localX,
            y: localY,
            // An auto-layout parent would pull the node into the flow and shove
            // its siblings; float it (Figma absolute) so it lands where placed,
            // mirroring drag-drop nesting in endDrag.
            ...(parentAutoLayout ? { layoutPositioning: 'ABSOLUTE' as const } : {}),
          };
          target = resolved;
        }
      }

      const nextDoc = mutAddNode(doc, placed, target);
      set({
        doc: nextDoc,
        selectedIds: new Set([placed.id]),
        renderVersion: get().renderVersion + 1,
      });
    },

    removeSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;
      // Never delete the email page itself: a root-level artboard is the
      // document's page frame, so removing it would gut the email. Drop any
      // such node from the deletion set — this covers every entry point (the
      // layers-panel trash, the Delete/Backspace shortcut, context menus).
      const deletable = new Set(
        [...selectedIds].filter((id) => {
          const n = doc.nodes[id];
          return !(
            n &&
            n.parentId == null &&
            doc.rootChildren.includes(id) &&
            n.pluginData?.['isArtboard'] === true
          );
        }),
      );
      if (deletable.size === 0) return;
      get().pushHistory();
      // Deleting a top-level band of an ABSOLUTE (NONE-layout) artboard must
      // close the vertical gap it occupied — insertSectionFrame pushed every
      // band below it down by the band's height, and auto-layout reflow
      // doesn't exist here to pull them back. Without this the email keeps a
      // band-sized hole of whitespace forever. VERTICAL artboards skip this
      // (the layout pass reflows the stack on its own).
      let workingDoc = doc;
      const rootId = doc.rootChildren.length === 1 ? doc.rootChildren[0] : null;
      const artboard = rootId ? doc.nodes[rootId] : null;
      if (artboard && artboard.type === 'FRAME' && (artboard.layoutMode ?? 'NONE') === 'NONE') {
        // Bottom-up so each gap close sees the positions left by the previous.
        const removedBands = [...deletable]
          .map((id) => workingDoc.nodes[id])
          .filter(
            (n): n is SceneNode =>
              !!n &&
              n.parentId === rootId &&
              (n.type === 'FRAME' || n.type === 'GROUP') &&
              n.slotRole !== 'link-zone' &&
              // Only full-width section bands take a slot in the vertical
              // stack. A floating artboard-level decoration (badge, overlay)
              // owns no band of space — deleting it must not shift anything.
              n.width >= (artboard.width || 600) * 0.9
          )
          .sort((a, b) => b.y - a.y);
        for (const band of removedBands) {
          const liveBand = workingDoc.nodes[band.id];
          if (!liveBand) continue;
          const bandBottom = liveBand.y + liveBand.height;
          const patches = new Map<string, Partial<SceneNode>>();
          for (const cid of artboard.children ?? []) {
            if (cid === band.id || deletable.has(cid)) continue;
            const child = workingDoc.nodes[cid];
            if (child && child.y >= bandBottom - 0.5) {
              patches.set(cid, { y: child.y - liveBand.height });
            }
          }
          if (patches.size) workingDoc = mutUpdateNodes(workingDoc, patches);
        }
      }
      // commitRootSize so a hugging artboard shrinks back around the
      // remaining content instead of keeping the deleted band's height.
      const nextDoc = commitRootSize(mutRemoveNodes(workingDoc, [...deletable]));
      set({
        doc: nextDoc,
        selectedIds: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    moveZOrder(direction) {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;
      get().pushHistory();
      let nextDoc = doc;
      for (const id of selectedIds) {
        nextDoc = mutMoveZOrder(nextDoc, id, direction);
      }
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    moveNodeToIndex(nodeId, newIndex) {
      const { doc } = get();
      get().pushHistory();
      const nextDoc = mutMoveNodeToIndex(doc, nodeId, newIndex);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    toggleSelectedFloating() {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const id = [...selectedIds][0];
      const node = doc.nodes[id];
      if (!node?.parentId) return;
      get().pushHistory();
      if (node.layoutPositioning === 'ABSOLUTE') {
        // Re-dock into the flow: drop the absolute flag and let layout reflow it
        // back into the stack (its x/y get recomputed).
        const nextDoc = commitRootSize(
          mutUpdateNode(doc, id, { layoutPositioning: 'AUTO' as const })
        );
        set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
        return;
      }
      // Float: mark absolute and preserve the current on-screen position so it
      // doesn't jump as it leaves the flow. Pin the parent's size FIRST so a
      // hugging frame doesn't collapse and shove its other children when this
      // child leaves the stack (it stays a fixed-size frame after).
      // Re-fit groups first so the laid-out doc read here and the doc written
      // below agree on where a group's origin sits (see tightenGroups).
      const tight = tightenGroups(doc);
      const laidOut = computeLayout(tight);
      const world = getNodeWorldBounds(laidOut, id);
      let d = tight;
      const parent = tight.nodes[node.parentId];
      if (parent && (parent.layoutMode === 'VERTICAL' || parent.layoutMode === 'HORIZONTAL')) {
        const lp = laidOut.nodes[parent.id];
        d = mutUpdateNode(d, parent.id, {
          width: lp?.width ?? parent.width,
          height: lp?.height ?? parent.height,
          layoutSizingHorizontal: 'FIXED' as const,
          layoutSizingVertical: 'FIXED' as const,
        });
      }
      d = mutUpdateNode(d, id, { layoutPositioning: 'ABSOLUTE' as const });
      const offset = getParentWorldOffset(computeLayout(d), id);
      d = mutUpdateNode(d, id, { x: world.x - offset.x, y: world.y - offset.y });
      set({ doc: commitRootSize(d), renderVersion: get().renderVersion + 1 });
    },

    reparentNode(nodeId, newParentId, insertIndex) {
      const { doc } = get();
      get().pushHistory();
      const nextDoc = mutReparentNode(doc, nodeId, newParentId, insertIndex);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    setSelectedAutoLayout(on, direction) {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;
      const laidOut = computeLayout(doc);
      let d = doc;
      let changed = false;
      for (const id of selectedIds) {
        const node = d.nodes[id];
        if (!node || (node.type !== 'FRAME' && node.type !== 'GROUP')) continue;
        // GRID counts as auto-layout here so the "off" toggle bakes each child's
        // laid-out cell geometry and pins the frame height, same as flow modes.
        const isAuto =
          node.layoutMode === 'VERTICAL' ||
          node.layoutMode === 'HORIZONTAL' ||
          node.layoutMode === 'GRID';
        if (on) {
          if (isAuto && !direction) continue; // already auto-layout, nothing asked
          // Add / re-enable auto-layout: flow the children and hug content again.
          d = mutUpdateNode(d, id, {
            layoutMode:
              direction ?? (node.layoutMode === 'HORIZONTAL' ? 'HORIZONTAL' : 'VERTICAL'),
            layoutSizingVertical: 'HUG' as const,
          });
          changed = true;
        } else {
          if (!isAuto) continue; // already free
          // Remove auto-layout (Figma's "Remove auto layout"): bake each child's
          // current laid-out geometry so nothing jumps out of the flow, and PIN
          // the frame to its current size — a free frame can no longer hug its
          // content, so without this it collapses (the band-shrink / footer-clip
          // bug class).
          const laid = laidOut.nodes[id];
          for (const cid of node.children ?? []) {
            const lc = laidOut.nodes[cid];
            if (!lc) continue;
            d = mutUpdateNode(d, cid, {
              x: lc.x,
              y: lc.y,
              width: lc.width,
              height: lc.height,
              layoutSizingHorizontal: 'FIXED' as const,
              layoutSizingVertical: 'FIXED' as const,
            });
          }
          d = mutUpdateNode(d, id, {
            layoutMode: 'NONE' as const,
            layoutSizingVertical: 'FIXED' as const,
            height: laid?.height ?? node.height,
          });
          changed = true;
        }
      }
      if (!changed) return;
      get().pushHistory();
      set({ doc: commitRootSize(d), renderVersion: get().renderVersion + 1 });
    },

    groupSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size < 2) return;
      get().pushHistory();
      const nextDoc = mutGroupNodes(doc, [...selectedIds]);
      // Find the newly created group (the node that wasn't in the old doc)
      const newGroupId = Object.keys(nextDoc.nodes).find((id) => !doc.nodes[id]);
      set({
        doc: nextDoc,
        selectedIds: newGroupId ? new Set([newGroupId]) : new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    ungroupSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const groupId = [...selectedIds][0];
      const group = doc.nodes[groupId];
      if (!group || (group.type !== 'GROUP' && group.type !== 'FRAME')) return;

      get().pushHistory();
      const childIds = group.children ?? [];
      const nextDoc = mutUngroupNode(doc, groupId);
      set({
        doc: nextDoc,
        selectedIds: new Set(childIds),
        renderVersion: get().renderVersion + 1,
      });
    },

    frameSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;
      const ids = [...selectedIds];
      const first = doc.nodes[ids[0]];
      if (!first) return;
      const parentId = first.parentId ?? null;

      // Compute the selection AABB in the shared parent's coordinate space
      // (node.x/y are parent-local). All nodes must share a parent, like group.
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const id of ids) {
        const n = doc.nodes[id];
        if (!n || (n.parentId ?? null) !== parentId) return;
        minX = Math.min(minX, n.x);
        minY = Math.min(minY, n.y);
        maxX = Math.max(maxX, n.x + n.width);
        maxY = Math.max(maxY, n.y + n.height);
      }
      if (!Number.isFinite(minX)) return;

      get().pushHistory();
      const frameId = generateNodeId();
      const frame: SceneNode = {
        id: frameId,
        type: 'FRAME',
        name: 'Frame',
        parentId,
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        // Free-form (no auto-layout) so children keep their absolute positions,
        // and transparent (no fills) so wrapping never paints over content.
        layoutMode: 'NONE',
        fills: [],
        children: [...ids],
      } as SceneNode;

      const nextNodes: Record<string, SceneNode> = { ...doc.nodes, [frameId]: frame };
      const idSet = new Set(ids);
      // Recompute each child's position relative to the new frame origin.
      for (const id of ids) {
        const n = nextNodes[id];
        nextNodes[id] = { ...n, x: n.x - minX, y: n.y - minY, parentId: frameId };
      }

      // Replace the selected nodes in their sibling list with the new frame,
      // keeping the frame where the first selected node sat.
      const siblingList = parentId
        ? [...(doc.nodes[parentId]?.children ?? [])]
        : [...doc.rootChildren];
      const firstIdx = siblingList.findIndex((id) => idSet.has(id));
      const filtered = siblingList.filter((id) => !idSet.has(id));
      filtered.splice(Math.max(0, firstIdx), 0, frameId);

      let nextDoc: SceneDocument;
      if (parentId) {
        nextNodes[parentId] = { ...nextNodes[parentId], children: filtered };
        nextDoc = { ...doc, nodes: nextNodes };
      } else {
        nextDoc = { ...doc, nodes: nextNodes, rootChildren: filtered };
      }

      set({
        doc: commitRootSize(nextDoc),
        selectedIds: new Set([frameId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    replaceImageWithVectorizedSvg(nodeId, svg, sourceWidth, sourceHeight) {
      const { doc } = get();
      const imageNode = doc.nodes[nodeId];
      if (!imageNode || imageNode.type !== 'IMAGE') return;
      if (sourceWidth <= 0 || sourceHeight <= 0) return;

      const xml = new DOMParser().parseFromString(svg, 'image/svg+xml');
      const pathEls = Array.from(xml.querySelectorAll('path'));
      if (pathEls.length === 0) return;

      // vtracer emits each <path> with a transform="translate(tx,ty)" that places the
      // path's local-d coords into the source image's coordinate space. We parse the
      // translate, add it to the path's bounds, and discard the transform attribute —
      // the renderer scales each path's tight bounds into its node's w/h, so as long
      // as the node is sized & positioned in source coords, the d-string can stay local.
      const TRANSLATE_RE = /^\s*translate\(\s*(-?\d+(?:\.\d+)?)[ ,]+(-?\d+(?:\.\d+)?)\s*\)\s*$/;
      const parseTranslate = (raw: string | null): { tx: number; ty: number } => {
        if (!raw) return { tx: 0, ty: 0 };
        const m = TRANSLATE_RE.exec(raw);
        if (!m) return { tx: 0, ty: 0 };
        return { tx: parseFloat(m[1]), ty: parseFloat(m[2]) };
      };

      const groupId = generateNodeId();
      const childIds: string[] = [];
      const childNodes: SceneNode[] = [];

      for (const el of pathEls) {
        const d = el.getAttribute('d');
        if (!d) continue;
        const fill = el.getAttribute('fill') ?? '#000000';
        const bounds = getSvgPathBounds(d);
        if (
          !Number.isFinite(bounds.width) ||
          !Number.isFinite(bounds.height) ||
          bounds.width <= 0 ||
          bounds.height <= 0
        )
          continue;

        const { tx, ty } = parseTranslate(el.getAttribute('transform'));
        const sourceX = bounds.x + tx;
        const sourceY = bounds.y + ty;

        const childId = generateNodeId();
        childIds.push(childId);
        childNodes.push({
          id: childId,
          type: 'PATH',
          parentId: groupId,
          x: (sourceX / sourceWidth) * imageNode.width,
          y: (sourceY / sourceHeight) * imageNode.height,
          width: (bounds.width / sourceWidth) * imageNode.width,
          height: (bounds.height / sourceHeight) * imageNode.height,
          pathData: d,
          fills: [{ type: 'SOLID', color: fill }],
        } as SceneNode);
      }

      if (childNodes.length === 0) return;

      const group: SceneNode = {
        id: groupId,
        type: 'GROUP',
        name: imageNode.name ?? 'Vectorized image',
        parentId: imageNode.parentId,
        x: imageNode.x,
        y: imageNode.y,
        width: imageNode.width,
        height: imageNode.height,
        children: childIds,
      } as SceneNode;

      get().pushHistory();

      const nextNodes = { ...doc.nodes };
      delete nextNodes[nodeId];
      nextNodes[groupId] = group;
      for (const c of childNodes) nextNodes[c.id] = c;

      let nextRootChildren = doc.rootChildren;
      if (imageNode.parentId) {
        const parent = nextNodes[imageNode.parentId];
        if (parent) {
          const childList = parent.children ?? [];
          const idx = childList.indexOf(nodeId);
          const replaced =
            idx >= 0
              ? [...childList.slice(0, idx), groupId, ...childList.slice(idx + 1)]
              : [...childList, groupId];
          nextNodes[imageNode.parentId] = { ...parent, children: replaced };
        }
      } else {
        const idx = doc.rootChildren.indexOf(nodeId);
        nextRootChildren =
          idx >= 0
            ? [...doc.rootChildren.slice(0, idx), groupId, ...doc.rootChildren.slice(idx + 1)]
            : [...doc.rootChildren, groupId];
      }

      set({
        doc: { ...doc, nodes: nextNodes, rootChildren: nextRootChildren },
        selectedIds: new Set([groupId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Document-level ────────────────────────────────────────────

    updateDocumentSize(width, height) {
      get().pushHistory();
      const doc = get().doc;
      let nextDoc: SceneDocument = { ...doc, width, height };
      // Single-root artboard docs: the root frame is the page, so a doc-size
      // edit must land on the frame too (otherwise syncDocSizeToRoot reverts
      // it on the next render pass). Height is layout-owned for hugging
      // artboards — only width is writable there.
      if (doc.rootChildren.length === 1) {
        const root = doc.nodes[doc.rootChildren[0]];
        if (root?.type === 'FRAME' && root.pluginData?.['isArtboard'] === true) {
          const hugsHeight = root.layoutSizingVertical === 'HUG';
          nextDoc = {
            ...nextDoc,
            ...(hugsHeight ? { height: doc.height } : {}),
            nodes: {
              ...doc.nodes,
              [root.id]: {
                ...root,
                width,
                ...(hugsHeight ? {} : { height }),
              },
            },
          };
          nextDoc = commitRootSize(nextDoc);
        }
      }
      set({
        doc: nextDoc,
        renderVersion: get().renderVersion + 1,
      });
    },

    updateDocumentFills(fills) {
      get().pushHistory();
      set({
        doc: { ...get().doc, fills },
        renderVersion: get().renderVersion + 1,
      });
    },

    addTemplateAsGroup(incoming, name) {
      const { doc } = get();
      // Center horizontally, place at vertical center
      const centerX = doc.width / 2;
      const centerY = doc.height / 2;

      get().pushHistory();
      const result = mutAddSceneDocumentAsGroup(doc, incoming, name, { x: centerX, y: centerY });

      // Auto-fit document height to content
      let nextDoc = result.doc;
      let maxBottom = 0;
      for (const id of nextDoc.rootChildren) {
        const n = nextDoc.nodes[id];
        if (!n || n.slotRole === 'link-zone') continue;
        maxBottom = Math.max(maxBottom, n.y + n.height);
      }
      if (maxBottom !== nextDoc.height) {
        nextDoc = { ...nextDoc, height: maxBottom };
      }

      set({
        doc: nextDoc,
        selectedIds: new Set([result.groupId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Link zones ────────────────────────────────────────────────

    addLinkZone() {
      get().pushHistory();
      const nextDoc = mutAddLinkZone(get().doc);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    removeLinkZone(nodeId) {
      get().pushHistory();
      const nextDoc = mutRemoveLinkZone(get().doc, nodeId);
      set({
        doc: nextDoc,
        selectedIds: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    setLinkZoneLayout(layoutId) {
      get().pushHistory();
      const nextDoc = mutSetLinkZoneLayout(get().doc, layoutId);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    getLinkZoneNodes() {
      return mutGetLinkZoneNodes(get().doc);
    },

    setLinkZonesVisible(visible) {
      // Clear selection when switching modes so stale zone/node selections don't linger
      set({
        linkZonesVisible: visible,
        selectedIds: new Set(),
        hoveredId: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    toggleGuidesVisible() {
      set({
        guidesVisible: !get().guidesVisible,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Clipboard ──────────────────────────────────────────────────

    copySelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;

      const { nodes, newIds } = mutCloneNodes(doc, [...selectedIds]);
      // The stamp lets a later paste recognize these exact nodes coming back
      // off the system clipboard, and prefer this raw (token-bearing) copy over
      // the baked one in the payload.
      set({ clipboardNodes: nodes, clipboardTopIds: newIds, clipboardStamp: newClipboardStamp() });
    },

    pasteClipboard(centerPoint) {
      const { clipboardNodes, clipboardTopIds, enteredGroupId } = get();
      if (!clipboardNodes || clipboardTopIds.length === 0) return;

      const placed = buildPaste(
        get().doc,
        clipboardNodes,
        clipboardTopIds,
        enteredGroupId,
        centerPoint,
      );
      if (!placed) return;

      get().pushHistory();
      // Re-seed the clipboard with the fresh nodes so the next paste offsets
      // from these rather than stacking on the originals.
      set({
        doc: placed.doc,
        selectedIds: new Set(placed.newIds),
        clipboardNodes: placed.nodes,
        clipboardTopIds: placed.newIds,
        renderVersion: get().renderVersion + 1,
      });
    },

    pasteExternalNodes(nodes, topIds, stamp, centerPoint) {
      const { enteredGroupId } = get();
      const placed = buildPaste(get().doc, nodes, topIds, enteredGroupId, centerPoint);
      if (!placed) return;

      get().pushHistory();
      // Adopt the payload as this editor's clipboard, stamp included: a repeat
      // Cmd+V then matches the stamp still sitting on the system clipboard and
      // takes the in-memory path, so it cascades instead of re-pasting the
      // original payload at the same spot every time.
      set({
        doc: placed.doc,
        selectedIds: new Set(placed.newIds),
        clipboardNodes: placed.nodes,
        clipboardTopIds: placed.newIds,
        clipboardStamp: stamp,
        renderVersion: get().renderVersion + 1,
      });
    },

    duplicateSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;

      get().pushHistory();

      // A single top-level section band duplicates as the NEXT band: the
      // copy lands directly below the original and the bands beneath shift
      // down, the way auto-layout reflow would.
      if (selectedIds.size === 1) {
        const banded = duplicateBandBelow(doc, [...selectedIds][0]);
        if (banded) {
          set({
            doc: banded.doc,
            selectedIds: new Set([banded.newId]),
            renderVersion: get().renderVersion + 1,
          });
          return;
        }
      }

      // Figma semantics: each clone is a sibling inserted right after its
      // original — never appended to the entered group or the document root,
      // which nested duplicates inside whatever container was open.
      const { doc: nextDoc, newIds } = mutDuplicateAsSiblings(doc, [...selectedIds]);
      set({
        doc: nextDoc,
        selectedIds: new Set(newIds),
        renderVersion: get().renderVersion + 1,
      });
    },

    nudge(dx, dy) {
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) return;
      get().pushHistory();
      const patches = new Map<string, Partial<SceneNode>>();
      for (const id of selectedIds) {
        const node = doc.nodes[id];
        if (node) {
          patches.set(id, { x: node.x + dx, y: node.y + dy });
        }
      }
      const nextDoc = mutUpdateNodes(doc, patches);
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    alignSelected(edge) {
      const { doc, selectedIds } = get();
      if (selectedIds.size < 2) return;
      const nextDoc = alignNodes(doc, selectedIds, edge);
      if (nextDoc === doc) return;
      get().pushHistory();
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    distributeSelected(axis) {
      const { doc, selectedIds } = get();
      if (selectedIds.size < 3) return;
      const nextDoc = distributeNodes(doc, selectedIds, axis);
      if (nextDoc === doc) return;
      get().pushHistory();
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    tidyUpSelected() {
      const { doc, selectedIds } = get();
      if (selectedIds.size < 2) return;
      const nextDoc = tidyUpNodes(doc, selectedIds);
      if (nextDoc === doc) return;
      get().pushHistory();
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    flipSelectedLayout() {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 1) return;
      const id = [...selectedIds][0];
      const node = doc.nodes[id];
      if (!node || (node.type !== 'FRAME' && node.type !== 'GROUP')) return;
      const childIds = (node.children ?? []).filter((cid) => doc.nodes[cid]);
      if (childIds.length < 2) return;

      get().pushHistory();

      const flipAlign = (a?: string) =>
        a === 'left' ? ('right' as const) : a === 'right' ? ('left' as const) : undefined;

      if (node.layoutMode === 'HORIZONTAL') {
        // Auto-layout row: mirroring = reversing the children order (and the
        // horizontal paddings). Positions re-derive on the next layout pass.
        const nextNodes: Record<string, SceneNode> = { ...doc.nodes };
        nextNodes[id] = {
          ...node,
          children: [...childIds].reverse(),
          layoutPaddingLeft: node.layoutPaddingRight,
          layoutPaddingRight: node.layoutPaddingLeft,
        };
        for (const cid of childIds) {
          const child = nextNodes[cid];
          const flipped = child.type === 'TEXT' ? flipAlign(child.textAlign) : undefined;
          if (flipped) nextNodes[cid] = { ...child, textAlign: flipped };
        }
        set({ doc: { ...doc, nodes: nextNodes }, renderVersion: get().renderVersion + 1 });
        return;
      }

      // Absolute container (the zig-zag image|text bands): mirror each direct
      // child's x across the frame width. Content itself is not mirrored —
      // this flips the LAYOUT, not the pixels (unlike Figma's Shift+H, which
      // mirrors fills/text too). Direct TEXT children swap left/right align
      // so copy reads correctly on its new side.
      const patches = new Map<string, Partial<SceneNode>>();
      for (const cid of childIds) {
        const child = doc.nodes[cid];
        const patch: Partial<SceneNode> = {
          x: node.width - child.x - child.width,
        };
        if (child.type === 'TEXT') {
          const flipped = flipAlign(child.textAlign);
          if (flipped) patch.textAlign = flipped;
        }
        patches.set(cid, patch);
      }
      set({
        doc: mutUpdateNodes(doc, patches),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Viewport ─────────────────────────────────────────────────────

    setZoom(newZoom, focalScreenPoint) {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      const { zoom, panOffset } = get();

      if (focalScreenPoint) {
        // Zoom toward the focal point
        const sceneX = (focalScreenPoint.x - panOffset.x) / zoom;
        const sceneY = (focalScreenPoint.y - panOffset.y) / zoom;
        set({
          zoom: clamped,
          panOffset: {
            x: focalScreenPoint.x - sceneX * clamped,
            y: focalScreenPoint.y - sceneY * clamped,
          },
          renderVersion: get().renderVersion + 1,
        });
      } else {
        set({ zoom: clamped, renderVersion: get().renderVersion + 1 });
      }
    },

    zoomToFit(viewportWidth, viewportHeight) {
      if (viewportWidth <= 0 || viewportHeight <= 0) return;
      const { doc } = get();
      const view = computeFitView(
        { x: 0, y: 0, width: doc.width, height: doc.height },
        viewportWidth,
        viewportHeight,
        1
      );
      set({ ...view, renderVersion: get().renderVersion + 1 });
    },

    zoomToSelection(viewportWidth, viewportHeight) {
      if (viewportWidth <= 0 || viewportHeight <= 0) return;
      const { doc, selectedIds } = get();
      if (selectedIds.size === 0) {
        get().zoomToFit(viewportWidth, viewportHeight);
        return;
      }
      // Union the AABBs of every selected node (already rotation-aware).
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (const id of selectedIds) {
        if (!doc.nodes[id]) continue;
        const b = getNodeAABB(doc, id);
        minX = Math.min(minX, b.x);
        minY = Math.min(minY, b.y);
        maxX = Math.max(maxX, b.x + b.width);
        maxY = Math.max(maxY, b.y + b.height);
      }
      if (!isFinite(minX)) {
        get().zoomToFit(viewportWidth, viewportHeight);
        return;
      }
      const view = computeFitView(
        { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
        viewportWidth,
        viewportHeight,
        MAX_ZOOM
      );
      set({ ...view, renderVersion: get().renderVersion + 1 });
    },

    // ── History ──────────────────────────────────────────────────────

    pushHistory() {
      const { doc, undoStack } = get();
      const nextStack = [...undoStack, structuredClone(doc)];
      if (nextStack.length > MAX_HISTORY) {
        nextStack.shift();
      }
      set({ undoStack: nextStack, redoStack: [] });
    },

    undo() {
      const { undoStack, doc, redoStack } = get();
      if (undoStack.length === 0) return;

      const prev = undoStack[undoStack.length - 1];
      set({
        doc: prev,
        undoStack: undoStack.slice(0, -1),
        redoStack: [...redoStack, structuredClone(doc)],
        selectedIds: new Set(),
        // Cancel any in-flight interaction: a Cmd+Z mid-drag would otherwise
        // leave dragNodeStarts pointing at the undone doc and the next
        // pointermove would patch ghost positions onto the restored one.
        interactionMode: 'idle',
        dragStart: null,
        dragNodeStarts: new Map(),
        resizeStartBounds: null,
        multiResizeStarts: null,
        snapGuides: [],
        dragReorder: null,
        dragDropTarget: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    redo() {
      const { redoStack, doc, undoStack } = get();
      if (redoStack.length === 0) return;

      const next = redoStack[redoStack.length - 1];
      set({
        doc: next,
        redoStack: redoStack.slice(0, -1),
        undoStack: [...undoStack, structuredClone(doc)],
        selectedIds: new Set(),
        interactionMode: 'idle',
        dragStart: null,
        dragNodeStarts: new Map(),
        resizeStartBounds: null,
        multiResizeStarts: null,
        snapGuides: [],
        dragReorder: null,
        dragDropTarget: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Image crop mode ───────────────────────────────────────────

    enterCropMode(nodeId) {
      const { doc } = get();
      const node = doc.nodes[nodeId];
      const target = resolveCropTarget(node);
      if (!node || !target) return;

      const cached = getCachedImage(target.src, Math.max(node.width, node.height));
      if (!cached) return; // image must be loaded

      const imgW = cached.width();
      const imgH = cached.height();

      // Pin a transform on entry so the rendered position survives subsequent
      // frame mutations.
      let nextDoc = doc;
      if (!target.transform) {
        const transform = computeDefaultTransform(imgW, imgH, node.width, node.height, target.fit);
        get().pushHistory();
        nextDoc = mutUpdateNode(doc, nodeId, cropTransformPatch(node, target.fillIndex, transform));
      }

      set({
        doc: nextDoc,
        croppingNodeId: nodeId,
        croppingFillIndex: target.fillIndex,
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    exitCropMode() {
      set({
        croppingNodeId: null,
        croppingFillIndex: null,
        cropDragStart: null,
        cropStartTransform: null,
        cropResizeHandle: null,
        cropStartFrame: null,
        interactionMode: 'idle',
        renderVersion: get().renderVersion + 1,
      });
    },

    startCropDrag(scenePoint) {
      const { croppingNodeId, croppingFillIndex, doc } = get();
      if (!croppingNodeId) return;
      const transform = readCropTransform(doc.nodes[croppingNodeId], croppingFillIndex);
      if (!transform) return;

      set({
        interactionMode: 'cropping',
        cropDragStart: scenePoint,
        cropStartTransform: { ...transform },
      });
    },

    updateCropDrag(scenePoint) {
      const { cropDragStart, cropStartTransform, croppingNodeId, croppingFillIndex, doc } = get();
      if (!cropDragStart || !cropStartTransform || !croppingNodeId) return;

      const node = doc.nodes[croppingNodeId];
      if (!node) return;

      const deltaX = scenePoint.x - cropDragStart.x;
      const deltaY = scenePoint.y - cropDragStart.y;

      const newTransform = panTransform(cropStartTransform, deltaX, deltaY);

      const nextDoc = mutUpdateNode(
        doc,
        croppingNodeId,
        cropTransformPatch(node, croppingFillIndex, newTransform)
      );
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    endCropDrag() {
      set({
        interactionMode: 'idle',
        cropDragStart: null,
        cropStartTransform: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    startCropResize(handle, scenePoint) {
      const { croppingNodeId, croppingFillIndex, doc } = get();
      if (!croppingNodeId) return;
      const node = doc.nodes[croppingNodeId];
      const transform = readCropTransform(node, croppingFillIndex);
      if (!node || !transform) return;

      get().pushHistory();
      set({
        interactionMode: 'cropResizing',
        cropResizeHandle: handle,
        cropDragStart: scenePoint,
        cropStartTransform: { ...transform },
        cropStartFrame: { x: node.x, y: node.y, width: node.width, height: node.height },
      });
    },

    updateCropResize(scenePoint) {
      const {
        cropDragStart,
        cropStartTransform,
        cropStartFrame,
        cropResizeHandle,
        croppingNodeId,
        croppingFillIndex,
        doc,
      } = get();
      if (
        !cropDragStart ||
        !cropStartTransform ||
        !cropStartFrame ||
        !cropResizeHandle ||
        !croppingNodeId
      )
        return;

      const node = doc.nodes[croppingNodeId];
      if (!node) return;

      const deltaX = scenePoint.x - cropDragStart.x;
      const deltaY = scenePoint.y - cropDragStart.y;

      const result = computeCropResize(
        cropResizeHandle,
        cropStartTransform,
        cropStartFrame,
        deltaX,
        deltaY
      );

      const nextDoc = mutUpdateNode(doc, croppingNodeId, {
        ...cropTransformPatch(node, croppingFillIndex, result.transform),
        x: result.frame.x,
        y: result.frame.y,
        width: result.frame.width,
        height: result.frame.height,
      });
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    endCropResize() {
      set({
        interactionMode: 'idle',
        cropDragStart: null,
        cropStartTransform: null,
        cropStartFrame: null,
        cropResizeHandle: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    zoomCroppedImage(scenePoint, factor) {
      const { croppingNodeId, croppingFillIndex, doc } = get();
      if (!croppingNodeId) return;
      const node = doc.nodes[croppingNodeId];
      const transform = readCropTransform(node, croppingFillIndex);
      if (!node || !transform) return;

      // Convert the scene-space anchor into frame-local coords (where
      // imageTransform.offsetX/Y live).
      const anchorFrameX = scenePoint.x - node.x;
      const anchorFrameY = scenePoint.y - node.y;

      const newTransform = zoomTransform(transform, anchorFrameX, anchorFrameY, factor);
      const nextDoc = mutUpdateNode(
        doc,
        croppingNodeId,
        cropTransformPatch(node, croppingFillIndex, newTransform)
      );
      set({ doc: nextDoc, renderVersion: get().renderVersion + 1 });
    },

    // ── Vector editing ────────────────────────────────────────────

    enterVectorEditMode(nodeId) {
      const { doc } = get();
      const node = doc.nodes[nodeId];
      if (!node) return;

      let vectorPaths: VectorPath[] | null = null;

      // Prefer structured vectorPaths from pluginData (lossless, from Figma import)
      const storedVps = node.pluginData?.vectorPaths as VectorPath[] | undefined;
      if (Array.isArray(storedVps) && storedVps.length > 0) {
        vectorPaths = storedVps;
      } else if (node.type === 'PATH' && node.pathData) {
        vectorPaths = parseSvgPathToVectorPaths(node.pathData);
      } else if (node.type === 'RECTANGLE') {
        vectorPaths = rectToVectorPaths(
          node.width,
          node.height,
          typeof node.cornerRadius === 'number' ? node.cornerRadius : undefined,
          node.cornerRadii
        );
      } else if (node.type === 'ELLIPSE') {
        vectorPaths = ellipseToVectorPaths(node.width, node.height);
      } else if (node.type === 'POLYGON') {
        vectorPaths = polygonToVectorPaths(node.width, node.height, node.sides ?? 6);
      } else if (node.type === 'TRAPEZOID') {
        vectorPaths = trapezoidToVectorPaths(node.width, node.height, node.skew ?? 0);
      } else if (node.type === 'SEAL') {
        vectorPaths = sealToVectorPaths(
          node.width,
          node.height,
          node.sides ?? 16,
          node.innerRadius ?? 0.85,
          node.bumpStyle ?? 'sharp'
        );
      } else if (node.type === 'STAR') {
        vectorPaths = starToVectorPaths(
          node.width,
          node.height,
          node.sides ?? 5,
          node.innerRadius ?? 0.4
        );
      }

      if (!vectorPaths || vectorPaths.length === 0) return;

      // Flatten non-PATH shapes: convert to PATH node (one-way, undo reverts)
      if (node.type !== 'PATH') {
        get().pushHistory();
        const pathData = vectorPathsToSvgPath(vectorPaths);
        const nextDoc = mutUpdateNode(doc, nodeId, {
          type: 'PATH' as any,
          pathData,
          pluginData: { ...(node.pluginData ?? {}), vectorPaths },
          // Clear shape-specific fields
          cornerRadius: undefined,
          cornerRadii: undefined,
          sides: undefined,
          innerRadius: undefined,
        });
        set({
          doc: nextDoc,
          vectorEditingNodeId: nodeId,
          vectorPaths,
          vectorSelectedPoints: new Set(),
          selectedIds: new Set([nodeId]),
          renderVersion: get().renderVersion + 1,
        });
      } else {
        set({
          vectorEditingNodeId: nodeId,
          vectorPaths,
          vectorSelectedPoints: new Set(),
          selectedIds: new Set([nodeId]),
          renderVersion: get().renderVersion + 1,
        });
      }
    },

    exitVectorEditMode() {
      const { vectorEditingNodeId, vectorPaths, doc } = get();

      // Write back edits to both pathData and pluginData.vectorPaths
      if (vectorEditingNodeId && vectorPaths) {
        const newPathData = vectorPathsToSvgPath(vectorPaths);
        const node = doc.nodes[vectorEditingNodeId];
        // Hand-editing the outline flattens a parametric organic shape:
        // shapeGen params no longer describe this path, so drop them (same
        // one-way rule as shape flattening in vectors.md).
        const { shapeGen: _droppedShapeGen, ...restPluginData } = node?.pluginData ?? {};
        const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, {
          pathData: newPathData,
          pluginData: { ...restPluginData, vectorPaths },
        });
        set({
          doc: nextDoc,
          vectorEditingNodeId: null,
          vectorPaths: null,
          vectorSelectedPoints: new Set(),
          vectorDragTarget: null,
          vectorDragStart: null,
          vectorDragPointStart: null,
          interactionMode: 'idle',
          renderVersion: get().renderVersion + 1,
        });
      } else {
        set({
          vectorEditingNodeId: null,
          vectorPaths: null,
          vectorSelectedPoints: new Set(),
          vectorDragTarget: null,
          vectorDragStart: null,
          vectorDragPointStart: null,
          interactionMode: 'idle',
          renderVersion: get().renderVersion + 1,
        });
      }
    },

    selectVectorPoint(pathIndex, pointIndex) {
      set({
        vectorSelectedPoints: new Set([`${pathIndex}:${pointIndex}`]),
        renderVersion: get().renderVersion + 1,
      });
    },

    toggleVectorPoint(pathIndex, pointIndex) {
      const { vectorSelectedPoints } = get();
      const key = `${pathIndex}:${pointIndex}`;
      const next = new Set(vectorSelectedPoints);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      set({ vectorSelectedPoints: next, renderVersion: get().renderVersion + 1 });
    },

    startVectorDrag(target, scenePoint) {
      const { vectorPaths } = get();
      if (!vectorPaths) return;

      const pt = vectorPaths[target.pathIndex]?.points[target.pointIndex];
      if (!pt) return;

      get().pushHistory();

      let startPos: { x: number; y: number };
      if (target.part === 'anchor') {
        startPos = { x: pt.x, y: pt.y };
      } else if (target.part === 'handleIn' && pt.handleIn) {
        startPos = { x: pt.x + pt.handleIn.x, y: pt.y + pt.handleIn.y };
      } else if (target.part === 'handleOut' && pt.handleOut) {
        startPos = { x: pt.x + pt.handleOut.x, y: pt.y + pt.handleOut.y };
      } else {
        return;
      }

      set({
        interactionMode: 'vectorDragging',
        vectorDragTarget: target,
        vectorDragStart: scenePoint,
        vectorDragPointStart: startPos,
      });
    },

    updateVectorDrag(scenePoint) {
      const {
        vectorDragTarget,
        vectorDragStart,
        vectorDragPointStart,
        vectorPaths,
        vectorEditingNodeId,
        doc,
      } = get();
      if (
        !vectorDragTarget ||
        !vectorDragStart ||
        !vectorDragPointStart ||
        !vectorPaths ||
        !vectorEditingNodeId
      )
        return;

      const node = doc.nodes[vectorEditingNodeId];
      if (!node?.pathData) return;

      // Compute delta in node-local space, then convert to path-native space
      const pathBounds = getSvgPathBounds(node.pathData);
      const scaleX = pathBounds.width > 0 ? node.width / pathBounds.width : 1;
      const scaleY = pathBounds.height > 0 ? node.height / pathBounds.height : 1;

      const dx = (scenePoint.x - vectorDragStart.x) / scaleX;
      const dy = (scenePoint.y - vectorDragStart.y) / scaleY;

      // Deep-clone vectorPaths for mutation
      const newPaths: VectorPath[] = vectorPaths.map((vp) => ({
        ...vp,
        points: vp.points.map((p) => ({
          ...p,
          handleIn: p.handleIn ? { ...p.handleIn } : null,
          handleOut: p.handleOut ? { ...p.handleOut } : null,
        })),
      }));

      const pt = newPaths[vectorDragTarget.pathIndex]?.points[vectorDragTarget.pointIndex];
      if (!pt) return;

      if (vectorDragTarget.part === 'anchor') {
        let newX = vectorDragPointStart.x + dx;
        let newY = vectorDragPointStart.y + dy;
        const gridSize = get().vectorGridSize;
        if (gridSize > 0) {
          newX = snapToGrid(newX, gridSize);
          newY = snapToGrid(newY, gridSize);
        }
        pt.x = newX;
        pt.y = newY;
      } else if (vectorDragTarget.part === 'handleIn') {
        const newAbsX = vectorDragPointStart.x + dx;
        const newAbsY = vectorDragPointStart.y + dy;
        pt.handleIn = { x: newAbsX - pt.x, y: newAbsY - pt.y };
        // Mirror for smooth/symmetric
        if (pt.cornerType === 'smooth' && pt.handleOut) {
          const len = Math.hypot(pt.handleOut.x, pt.handleOut.y);
          const inLen = Math.hypot(pt.handleIn!.x, pt.handleIn!.y);
          if (inLen > 0) {
            pt.handleOut = {
              x: (-pt.handleIn!.x / inLen) * len,
              y: (-pt.handleIn!.y / inLen) * len,
            };
          }
        } else if (pt.cornerType === 'symmetric' && pt.handleOut) {
          pt.handleOut = { x: -pt.handleIn!.x, y: -pt.handleIn!.y };
        }
      } else if (vectorDragTarget.part === 'handleOut') {
        const newAbsX = vectorDragPointStart.x + dx;
        const newAbsY = vectorDragPointStart.y + dy;
        pt.handleOut = { x: newAbsX - pt.x, y: newAbsY - pt.y };
        // Mirror for smooth/symmetric
        if (pt.cornerType === 'smooth' && pt.handleIn) {
          const len = Math.hypot(pt.handleIn.x, pt.handleIn.y);
          const outLen = Math.hypot(pt.handleOut!.x, pt.handleOut!.y);
          if (outLen > 0) {
            pt.handleIn = {
              x: (-pt.handleOut!.x / outLen) * len,
              y: (-pt.handleOut!.y / outLen) * len,
            };
          }
        } else if (pt.cornerType === 'symmetric' && pt.handleIn) {
          pt.handleIn = { x: -pt.handleOut!.x, y: -pt.handleOut!.y };
        }
      }

      // Regenerate pathData and update the document (dual-write for pluginData)
      const newPathData = vectorPathsToSvgPath(newPaths);
      const curNode = doc.nodes[vectorEditingNodeId];
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, {
        pathData: newPathData,
        pluginData: { ...(curNode?.pluginData ?? {}), vectorPaths: newPaths },
      });

      set({
        doc: nextDoc,
        vectorPaths: newPaths,
        renderVersion: get().renderVersion + 1,
      });
    },

    endVectorDrag() {
      set({
        interactionMode: 'idle',
        vectorDragTarget: null,
        vectorDragStart: null,
        vectorDragPointStart: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    updateVectorPaths(newPaths) {
      const { vectorEditingNodeId, doc } = get();
      if (!vectorEditingNodeId) return;
      get().pushHistory();
      const newPathData = vectorPathsToSvgPath(newPaths);
      const curNode = doc.nodes[vectorEditingNodeId];
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, {
        pathData: newPathData,
        pluginData: { ...(curNode?.pluginData ?? {}), vectorPaths: newPaths },
      });
      set({
        doc: nextDoc,
        vectorPaths: newPaths,
        renderVersion: get().renderVersion + 1,
      });
    },

    deleteVectorPoints() {
      const { vectorSelectedPoints, vectorPaths, vectorEditingNodeId, doc } = get();
      if (!vectorPaths || !vectorEditingNodeId || vectorSelectedPoints.size === 0) return;

      get().pushHistory();

      // Build set of points to delete per path
      const toDelete = new Map<number, Set<number>>();
      for (const key of vectorSelectedPoints) {
        const [pi, idx] = key.split(':').map(Number);
        if (!toDelete.has(pi)) toDelete.set(pi, new Set());
        toDelete.get(pi)!.add(idx);
      }

      const newPaths: VectorPath[] = [];
      for (let pi = 0; pi < vectorPaths.length; pi++) {
        const delSet = toDelete.get(pi);
        if (!delSet) {
          newPaths.push(vectorPaths[pi]);
          continue;
        }
        const remaining = vectorPaths[pi].points.filter((_, i) => !delSet.has(i));
        if (remaining.length >= 2) {
          newPaths.push({ ...vectorPaths[pi], points: remaining });
        }
        // Drop subpaths with < 2 points
      }

      if (newPaths.length === 0 || newPaths.every((p) => p.points.length === 0)) {
        // All points deleted -- remove the node entirely
        const nextDoc = mutRemoveNodes(doc, [vectorEditingNodeId]);
        set({
          doc: nextDoc,
          vectorEditingNodeId: null,
          vectorPaths: null,
          vectorSelectedPoints: new Set(),
          selectedIds: new Set(),
          renderVersion: get().renderVersion + 1,
        });
        return;
      }

      const newPathData = vectorPathsToSvgPath(newPaths);
      const curNode = doc.nodes[vectorEditingNodeId];
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, {
        pathData: newPathData,
        pluginData: { ...(curNode?.pluginData ?? {}), vectorPaths: newPaths },
      });

      set({
        doc: nextDoc,
        vectorPaths: newPaths,
        vectorSelectedPoints: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    addPointOnEdge(edge) {
      const { vectorPaths, vectorEditingNodeId, doc } = get();
      if (!vectorPaths || !vectorEditingNodeId) return;

      get().pushHistory();
      const newPath = insertPointOnSegment(vectorPaths[edge.pathIndex], edge.segmentIndex, edge.t);
      const newPaths = vectorPaths.map((vp, i) => (i === edge.pathIndex ? newPath : vp));
      const newPathData = vectorPathsToSvgPath(newPaths);
      const curNode = doc.nodes[vectorEditingNodeId];
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, {
        pathData: newPathData,
        pluginData: { ...(curNode?.pluginData ?? {}), vectorPaths: newPaths },
      });

      // Select the newly inserted point
      const newPointIdx = edge.segmentIndex + 1;
      set({
        doc: nextDoc,
        vectorPaths: newPaths,
        vectorSelectedPoints: new Set([`${edge.pathIndex}:${newPointIdx}`]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Pen tool ────────────────────────────────────────────────────

    penToolClick(scenePoint) {
      const { penCurrentPath, drawTool, doc } = get();
      if (drawTool !== 'PEN') return;

      if (!penCurrentPath) {
        // Start a new path
        const newPath: VectorPath = {
          points: [
            {
              x: scenePoint.x,
              y: scenePoint.y,
              handleIn: null,
              handleOut: null,
              cornerType: 'corner',
            },
          ],
          closed: false,
        };
        set({
          penCurrentPath: newPath,
          renderVersion: get().renderVersion + 1,
        });
        return;
      }

      // Check if clicking near the first point to close the path
      const first = penCurrentPath.points[0];
      if (penCurrentPath.points.length >= 3) {
        const dist = Math.hypot(scenePoint.x - first.x, scenePoint.y - first.y);
        if (dist < 10 / get().zoom) {
          // Close and finish
          const closedPath = { ...penCurrentPath, closed: true };
          finishPenPath(get, set, closedPath);
          return;
        }
      }

      // Add a new corner point
      const newPoint: import('./canvaskit-vector-points').VectorPoint = {
        x: scenePoint.x,
        y: scenePoint.y,
        handleIn: null,
        handleOut: null,
        cornerType: 'corner',
      };
      set({
        penCurrentPath: {
          ...penCurrentPath,
          points: [...penCurrentPath.points, newPoint],
        },
        renderVersion: get().renderVersion + 1,
      });
    },

    penToolDrag(scenePoint) {
      const { penCurrentPath } = get();
      if (!penCurrentPath || penCurrentPath.points.length === 0) return;

      const pts = penCurrentPath.points;
      const last = pts[pts.length - 1];
      const dx = scenePoint.x - last.x;
      const dy = scenePoint.y - last.y;

      const newPts = [...pts];
      newPts[newPts.length - 1] = {
        ...last,
        handleOut: { x: dx, y: dy },
        handleIn: { x: -dx, y: -dy },
        cornerType: 'smooth',
      };

      set({
        penCurrentPath: { ...penCurrentPath, points: newPts },
        penDragging: true,
        renderVersion: get().renderVersion + 1,
      });
    },

    penToolEndDrag() {
      set({ penDragging: false });
    },

    penToolFinish() {
      const { penCurrentPath } = get();
      if (!penCurrentPath || penCurrentPath.points.length < 2) {
        set({ penCurrentPath: null, drawTool: null, renderVersion: get().renderVersion + 1 });
        return;
      }
      finishPenPath(get, set, penCurrentPath);
    },

    // ── Text to outlines ────────────────────────────────────────────

    textToOutlines(nodeId, ck, fontMgr) {
      const { doc } = get();
      const node = doc.nodes[nodeId];
      if (!node || node.type !== 'TEXT' || !node.text) return;

      const text =
        node.textTransform === 'uppercase'
          ? node.text.toUpperCase()
          : node.textTransform === 'lowercase'
            ? node.text.toLowerCase()
            : node.text;

      const fontSize = node.fontSize ?? 16;
      const isBold = node.fontWeight != null && node.fontWeight >= 600;
      const fontFamily = node.fontFamily ?? 'Roboto';

      // Build SkFont
      const typeface = fontMgr.matchFamilyStyle(fontFamily, {
        weight: isBold ? 700 : 400,
        width: 5,
        slant: 0,
      });
      if (!typeface) return;

      const font = new ck.Font(typeface, fontSize);
      font.setEdging(ck.FontEdging.AntiAlias);

      // Get glyph IDs and positions
      const glyphIds = font.getGlyphIDs(text);
      const glyphWidths = font.getGlyphWidths(glyphIds);

      // Build a combined path for all glyphs
      const combinedPath = new ck.Path();
      const xOffset = 0;
      const lines = text.split('\n');
      let yOffset = 0;
      const lineHeight = fontSize * (node.lineHeight ?? 1.2);

      for (const line of lines) {
        const lineGlyphIds = font.getGlyphIDs(line);
        const lineWidths = font.getGlyphWidths(lineGlyphIds);
        let lineX = 0;

        // Text alignment offset
        const lineWidth = lineWidths.reduce((s: number, w: number) => s + w, 0);
        if (node.textAlign === 'center') {
          lineX = (node.width - lineWidth) / 2;
        } else if (node.textAlign === 'right') {
          lineX = node.width - lineWidth;
        }

        for (let i = 0; i < lineGlyphIds.length; i++) {
          const glyphPath = font.getGlyphPath(lineGlyphIds[i]);
          if (glyphPath) {
            const transform = ck.Matrix.translated(lineX, yOffset + fontSize);
            const transformedPath = glyphPath.copy().transform(...transform);
            combinedPath.op(transformedPath, ck.PathOp.Union);
            transformedPath.delete();
            glyphPath.delete();
          }
          lineX += lineWidths[i];
        }
        yOffset += lineHeight;
      }

      // Convert SkPath to SVG path string
      const svgPathData = combinedPath.toSVGString();
      combinedPath.delete();
      font.delete();

      if (!svgPathData) return;

      get().pushHistory();

      // Replace the TEXT node with a PATH node
      const fills = node.fills?.length
        ? node.fills
        : [{ type: 'SOLID' as const, color: '#000000' }];
      const nextDoc = mutUpdateNode(doc, nodeId, {
        type: 'PATH' as any,
        pathData: svgPathData,
        fills,
        text: undefined,
        fontSize: undefined,
        fontFamily: undefined,
        fontWeight: undefined,
        fontStyle: undefined,
        textAlign: undefined,
        verticalAlign: undefined,
        lineHeight: undefined,
        letterSpacing: undefined,
        textDecoration: undefined,
        textTransform: undefined,
      });

      set({
        doc: nextDoc,
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Boolean operations ──────────────────────────────────────────

    booleanOp(op, ck) {
      const { doc, selectedIds } = get();
      if (selectedIds.size !== 2) return;

      const ids = [...selectedIds];
      const nodeA = doc.nodes[ids[0]];
      const nodeB = doc.nodes[ids[1]];
      if (!nodeA || !nodeB) return;

      // Helper: get SkPath from a node
      const getSkPath = (node: SceneNode): any => {
        if (node.type === 'PATH' && node.pathData) {
          const p = ck.Path.MakeFromSVGString(node.pathData);
          if (p) {
            // Scale path to fit node bounds, then translate to world position
            const bounds = p.getBounds();
            const bw = bounds[2] - bounds[0];
            const bh = bounds[3] - bounds[1];
            const sx = bw > 0 ? node.width / bw : 1;
            const sy = bh > 0 ? node.height / bh : 1;
            const matrix = ck.Matrix.multiply(
              ck.Matrix.translated(node.x, node.y),
              ck.Matrix.scaled(sx, sy),
              ck.Matrix.translated(-bounds[0], -bounds[1])
            );
            p.transform(...matrix);
          }
          return p;
        }
        if (node.type === 'RECTANGLE') {
          const p = new ck.Path();
          if (node.cornerRadius) {
            p.addRRect(
              ck.RRectXY(
                ck.LTRBRect(node.x, node.y, node.x + node.width, node.y + node.height),
                node.cornerRadius,
                node.cornerRadius
              )
            );
          } else {
            p.addRect(ck.LTRBRect(node.x, node.y, node.x + node.width, node.y + node.height));
          }
          return p;
        }
        if (node.type === 'ELLIPSE') {
          const p = new ck.Path();
          p.addOval(ck.LTRBRect(node.x, node.y, node.x + node.width, node.y + node.height));
          return p;
        }
        // For STAR/POLYGON: convert to vector paths, then to SVG, then to SkPath
        let vps: VectorPath[] | null = null;
        if (node.type === 'STAR')
          vps = starToVectorPaths(
            node.width,
            node.height,
            node.sides ?? 5,
            node.innerRadius ?? 0.4
          );
        if (node.type === 'POLYGON')
          vps = polygonToVectorPaths(node.width, node.height, node.sides ?? 6);
        if (node.type === 'TRAPEZOID')
          vps = trapezoidToVectorPaths(node.width, node.height, node.skew ?? 0);
        if (node.type === 'SEAL')
          vps = sealToVectorPaths(
            node.width,
            node.height,
            node.sides ?? 16,
            node.innerRadius ?? 0.85,
            node.bumpStyle ?? 'sharp'
          );
        if (vps) {
          const svg = vectorPathsToSvgPath(vps);
          const p = ck.Path.MakeFromSVGString(svg);
          if (p) {
            const bounds = p.getBounds();
            const bw = bounds[2] - bounds[0];
            const bh = bounds[3] - bounds[1];
            const sx = bw > 0 ? node.width / bw : 1;
            const sy = bh > 0 ? node.height / bh : 1;
            const matrix = ck.Matrix.multiply(
              ck.Matrix.translated(node.x, node.y),
              ck.Matrix.scaled(sx, sy),
              ck.Matrix.translated(-bounds[0], -bounds[1])
            );
            p.transform(...matrix);
          }
          return p;
        }
        return null;
      };

      const pathA = getSkPath(nodeA);
      const pathB = getSkPath(nodeB);
      if (!pathA || !pathB) {
        pathA?.delete();
        pathB?.delete();
        return;
      }

      const ckOp =
        op === 'union'
          ? ck.PathOp.Union
          : op === 'subtract'
            ? ck.PathOp.Difference
            : op === 'intersect'
              ? ck.PathOp.Intersect
              : ck.PathOp.XOR;

      const success = pathA.op(pathB, ckOp);
      if (!success) {
        pathA.delete();
        pathB.delete();
        return;
      }

      const resultSvg = pathA.toSVGString();
      const resultBounds = pathA.getBounds();
      pathA.delete();
      pathB.delete();

      if (!resultSvg) return;

      get().pushHistory();

      // Create new PATH node from the result, remove the originals
      const newId = generateNodeId();
      const rx = resultBounds[0],
        ry = resultBounds[1];
      const rw = resultBounds[2] - resultBounds[0];
      const rh = resultBounds[3] - resultBounds[1];

      // Translate the SVG path so it starts at (0,0) relative to node position
      const translatedPath = ck.Path.MakeFromSVGString(resultSvg);
      if (translatedPath) {
        translatedPath.transform(...ck.Matrix.translated(-rx, -ry));
        const finalSvg = translatedPath.toSVGString();
        translatedPath.delete();

        const newNode: SceneNode = {
          id: newId,
          type: 'PATH',
          parentId: null,
          x: rx,
          y: ry,
          width: rw,
          height: rh,
          pathData: finalSvg,
          fills: nodeA.fills?.length ? [...nodeA.fills] : [{ type: 'SOLID', color: '#D1D5DB' }],
          name: `boolean-${op}`,
        };

        let nextDoc = mutRemoveNodes(doc, ids);
        nextDoc = mutAddNode(nextDoc, newNode);

        set({
          doc: nextDoc,
          selectedIds: new Set([newId]),
          renderVersion: get().renderVersion + 1,
        });
      }
    },

    // ── Freehand tool ─────────────────────────────────────────────

    freehandStart(scenePoint) {
      set({
        freehandPoints: [scenePoint],
        interactionMode: 'drawing',
        renderVersion: get().renderVersion + 1,
      });
    },

    freehandMove(scenePoint) {
      const { freehandPoints } = get();
      if (!freehandPoints) return;
      set({
        freehandPoints: [...freehandPoints, scenePoint],
        renderVersion: get().renderVersion + 1,
      });
    },

    freehandEnd() {
      const { freehandPoints, doc } = get();
      if (!freehandPoints || freehandPoints.length < 2) {
        set({
          freehandPoints: null,
          drawTool: null,
          interactionMode: 'idle',
          renderVersion: get().renderVersion + 1,
        });
        return;
      }

      get().pushHistory();
      const vectorPath = fitFreehandPath(freehandPoints);
      const pathData = vectorPathsToSvgPath([vectorPath]);
      const nodeId = generateNodeId();
      const node: SceneNode = {
        id: nodeId,
        type: 'PATH',
        parentId: null,
        x: 0,
        y: 0,
        width: doc.width,
        height: doc.height,
        pathData,
        fills: [],
        strokes: [{ type: 'SOLID', color: '#000000' }],
        strokeWeight: 2,
        name: 'freehand',
      };
      const nextDoc = mutAddNode(doc, node);
      set({
        doc: nextDoc,
        freehandPoints: null,
        drawTool: null,
        interactionMode: 'idle',
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Path simplification ─────────────────────────────────────────

    simplifyVectorPaths(tolerance) {
      const { vectorPaths, vectorEditingNodeId, doc } = get();
      if (!vectorPaths || !vectorEditingNodeId) return;
      get().pushHistory();
      const simplified = vectorPaths.map((vp) => simplifyPath(vp, tolerance));
      const newPathData = vectorPathsToSvgPath(simplified);
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, { pathData: newPathData });
      set({
        doc: nextDoc,
        vectorPaths: simplified,
        vectorSelectedPoints: new Set(),
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Grid snap ───────────────────────────────────────────────────

    setVectorGridSize(size) {
      set({ vectorGridSize: size });
    },

    snapSelectedPointsToGrid() {
      const { vectorPaths, vectorSelectedPoints, vectorEditingNodeId, vectorGridSize, doc } = get();
      if (
        !vectorPaths ||
        !vectorEditingNodeId ||
        vectorGridSize <= 0 ||
        vectorSelectedPoints.size === 0
      )
        return;
      get().pushHistory();
      const newPaths = vectorPaths.map((vp, vpi) => ({
        ...vp,
        points: vp.points.map((pt, i) => {
          if (!vectorSelectedPoints.has(`${vpi}:${i}`)) return pt;
          return {
            ...pt,
            x: snapToGrid(pt.x, vectorGridSize),
            y: snapToGrid(pt.y, vectorGridSize),
          };
        }),
      }));
      const newPathData = vectorPathsToSvgPath(newPaths);
      const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, { pathData: newPathData });
      set({ doc: nextDoc, vectorPaths: newPaths, renderVersion: get().renderVersion + 1 });
    },

    // ── SVG import ──────────────────────────────────────────────────

    importSvg(svgString) {
      const { doc } = get();
      const { paths, viewBox } = parseSvgToVectorPaths(svgString);
      if (paths.length === 0) return;

      get().pushHistory();
      const pathData = vectorPathsToSvgPath(paths);
      const vb = viewBox || { x: 0, y: 0, width: 200, height: 200 };
      const nodeId = generateNodeId();
      const node: SceneNode = {
        id: nodeId,
        type: 'PATH',
        parentId: null,
        x: (doc.width - vb.width) / 2,
        y: (doc.height - vb.height) / 2,
        width: vb.width,
        height: vb.height,
        pathData,
        fills: [{ type: 'SOLID', color: '#333333' }],
        name: 'svg-import',
      };
      const nextDoc = mutAddNode(doc, node);
      set({ doc: nextDoc, selectedIds: new Set([nodeId]), renderVersion: get().renderVersion + 1 });
    },

    // ── Copy/paste vector points ────────────────────────────────────

    copyVectorPoints() {
      const { vectorPaths, vectorSelectedPoints } = get();
      if (!vectorPaths || vectorSelectedPoints.size === 0) return;

      // Collect selected points into a mini VectorPath
      const copied: VectorPoint[] = [];
      for (const key of vectorSelectedPoints) {
        const [pi, idx] = key.split(':').map(Number);
        const pt = vectorPaths[pi]?.points[idx];
        if (pt)
          copied.push({
            ...pt,
            handleIn: pt.handleIn ? { ...pt.handleIn } : null,
            handleOut: pt.handleOut ? { ...pt.handleOut } : null,
          });
      }
      if (copied.length === 0) return;

      // Store in clipboard as a JSON string on the system clipboard
      const data = JSON.stringify(copied);
      navigator.clipboard.writeText(data).catch(() => {});
    },

    pasteVectorPoints() {
      const { vectorPaths, vectorEditingNodeId, doc } = get();
      if (!vectorPaths || !vectorEditingNodeId) return;

      navigator.clipboard.readText().then((text) => {
        try {
          const points = JSON.parse(text) as VectorPoint[];
          if (!Array.isArray(points) || points.length === 0) return;

          // Offset pasted points slightly
          const offset = 10;
          const offsetPts = points.map((p) => ({
            ...p,
            x: p.x + offset,
            y: p.y + offset,
          }));

          get().pushHistory();
          // Append to the first subpath
          const newPaths = vectorPaths.map((vp, i) =>
            i === 0 ? { ...vp, points: [...vp.points, ...offsetPts] } : vp
          );
          const newPathData = vectorPathsToSvgPath(newPaths);
          const nextDoc = mutUpdateNode(doc, vectorEditingNodeId, { pathData: newPathData });

          // Select the newly pasted points
          const startIdx = vectorPaths[0].points.length;
          const newSelection = new Set(offsetPts.map((_, i) => `0:${startIdx + i}`));

          set({
            doc: nextDoc,
            vectorPaths: newPaths,
            vectorSelectedPoints: newSelection,
            renderVersion: get().renderVersion + 1,
          });
        } catch {
          // Not valid vector point data
        }
      });
    },

    // ── Inline text editing ─────────────────────────────────────────

    startTextEdit(nodeId) {
      const node = get().doc.nodes[nodeId];
      if (!node || node.type !== 'TEXT') return;
      set({
        editingTextId: nodeId,
        selectedIds: new Set([nodeId]),
        renderVersion: get().renderVersion + 1,
      });
    },

    commitTextEdit(newText, measuredHeight, spans, list, measuredWidth) {
      const { editingTextId, doc } = get();
      if (!editingTextId) return;
      get().pushHistory();
      const node = doc.nodes[editingTextId];
      const patch: Record<string, unknown> = { text: newText };
      // Size the box from the measured content per the node's textAutoResize
      // intent. When absent, this reduces to the legacy grow-only-height rule.
      if (measuredHeight && node) {
        Object.assign(
          patch,
          textAutoResizeDimPatch(node.textAutoResize, node, measuredWidth ?? node.width, measuredHeight),
        );
      }
      // A link covering the whole text is what the node's own Link URL field
      // expresses, so promote it there while that field is empty: pasting a
      // hyperlink otherwise left the panel blank and a rasterized band with no
      // link zone. Promote rather than mirror — node.linkUrl wraps the text in
      // an <a> of its own, so a surviving span link would nest anchors. The span
      // keeps its resolved underline so the look doesn't change. A link over
      // part of the text stays span-only; promoting it would make the whole node
      // clickable.
      let nextSpans = spans;
      if (!node?.linkUrl && newText.length > 0 && spans?.length) {
        const hrefs = new Set(spans.filter((s) => s.link).map((s) => s.link));
        const whole = spans.find((s) => s.link && s.start === 0 && s.end === newText.length);
        if (hrefs.size === 1 && whole?.link) {
          patch.linkUrl = whole.link;
          nextSpans = spans.map(({ link, ...rest }) =>
            link ? { ...rest, underline: isUnderlined({ underline: rest.underline, link }) } : rest,
          );
        }
      }

      // Persist rich-text spans + list alongside other pluginData (textWarp, etc.).
      if (spans !== undefined || list !== undefined) {
        const nextPlugin = { ...(node?.pluginData ?? {}) } as Record<string, unknown>;
        const richText: Record<string, unknown> = {};
        if (nextSpans?.length) richText.spans = nextSpans;
        if (list) richText.list = list;
        if (Object.keys(richText).length) nextPlugin.richText = richText;
        else delete nextPlugin.richText;
        patch.pluginData = nextPlugin;
      }
      const nextDoc = mutUpdateNode(doc, editingTextId, patch);
      set({
        doc: nextDoc,
        editingTextId: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    cancelTextEdit() {
      set({
        editingTextId: null,
        renderVersion: get().renderVersion + 1,
      });
    },

    // ── Render ───────────────────────────────────────────────────────

    requestRender() {
      set({ renderVersion: get().renderVersion + 1 });
    },

    // ── Computed ─────────────────────────────────────────────────────

    getSelectedNodes() {
      const { doc, selectedIds } = get();
      return [...selectedIds].map((id) => doc.nodes[id]).filter((n): n is SceneNode => n != null);
    },
  }));
}

export type CanvasEditorStore = ReturnType<typeof createCanvasEditorStore>;
